import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { fluid } from '../styles/motion'; // [MOTION-A 2026-08-20] [FLUID-3 2026-10-12]
import { usePresence } from '../hooks/usePresence'; // [FLUID-3 2026-10-12]
import { fluidDialog, fluidScrim, fluidCascade } from './Fluid'; // [FLUID-3 2026-10-12]
// [BUTTON-STYLE 2026-09-30] [RANKING-ONLY 2026-10-27] pillTrack / pillSegment size the PIA "Sales | PM" switch
import { pillTrack, pillSegment } from '../styles/buttons';
// [LIQUID-GLASS 2026-11-02, user] the window, group cards, boxes and buttons use the shared liquid glass recipe
import {
  lgSurface, lgCard, lgPill, lgPress, lgSunken, lgSquircle, lgGreen,
  lgButtonPrimary, lgButtonSecondary, lgButtonDanger, LG_PRESS, LgPressProps,
} from '../styles/liquidGlass';
import { Payment, SplitPartInfo, PmRetentionShare } from '../types';
import { fetchPaymentLastEdit, PaymentLastEdit } from '../services/api';
import { toastError } from './toastService'; // [MAP-VALIDATION] 2026-06-23
import RetentionPmPopup, { PmStepPart, PmStepShare } from './RetentionPmPopup'; // [INV-SPLIT 2026-10-07] [SPLIT-RET-BY-PM 2026-10-08]
// [RANKING-ONLY 2026-10-27] Only the Scale Ranking logo is left: the Performance / Tech / Creative column headers (and
// GlassSelect, used only by the PIA Month / Dept dropdowns of that grid) went with the four-brand grid.
import { ReactComponent as RankingLogo } from '../assets/ranking.svg';


/* [PERF 2026-08-14] Locale formatters built ONCE — `toLocale*(locale, {options})`
 * constructs a fresh Intl formatter per call (measured 34us money / 90us date on V8,
 * vs 0.7us / 1.5us cached). Calls WITHOUT options are already fast and left alone. */
const MONEY_FMT = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
// IST, not UTC - these two render the audit timestamp in Asia/Kolkata. Named explicitly so
// nobody reads them as UTC formatters and "fixes" the timezone away.
const MONTH_SHORT_DAY_YEAR_IST_FMT = new Intl.DateTimeFormat('en-US', { timeZone: 'Asia/Kolkata', month: 'short', day: 'numeric', year: 'numeric' });
const TIME_IST_FMT = new Intl.DateTimeFormat('en-US', { timeZone: 'Asia/Kolkata', hour: 'numeric', minute: '2-digit', hour12: true });
// [PIA-PREVIEW 2026-08-26] Bare month + year for the PIA landing-month hint. UTC because the
// value it formats is a synthetic first-of-month, not a real instant.
const MONTH_YEAR_UTC_FMT = new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', month: 'short', year: 'numeric' });

/**
 * [PIA-PREVIEW 2026-08-26] Which month a PM PIA's renewal row will be created in.
 *
 * The rule, confirmed with the user 2026-08-26 and unchanged since the feature was built:
 * PIA Month N counts the payment's OWN month, so the renewal inventory row lands at
 * paymentMonth + N — the month the next payment is expected. Jun payment + term 2 covers
 * June and July, so the row appears in August. The server does this same arithmetic in
 * services/autoInventory.ts (addMonths(paymentMonth, pmPiaTerm)) and in the reconciler.
 *
 * ⚠ It is keyed on the payment's MONTH column, never payment_date: payments.month is stamped
 * on the Pacific calendar date, so a payment made in the first hours of a month can belong to
 * the previous one, and the inventory row follows the month, not the date on screen.
 *
 * Returns null when there is nothing true to say (no month on the row, no/invalid term).
 */
function piaLandingMonth(monthStr: string | null | undefined, term: string | undefined): string | null {
  const n = parseInt(String(term ?? ''), 10);
  if (!Number.isFinite(n) || n < 1) return null;
  return monthPlus(monthStr, n);
}

/** [RANKING-ONLY 2026-10-27] The payment's month moved on by `add` months, as "Oct 2026" (0 = its own month) — the
 *  arithmetic piaLandingMonth always used, now also for the PIA line's "Pays for Oct 2026 to Nov 2026". Keyed on the
 *  month column for the same reason. Null when the row has no usable month. */
function monthPlus(monthStr: string | null | undefined, add: number): string | null {
  if (!monthStr) return null;
  const [y, m] = monthStr.slice(0, 7).split('-').map(Number);
  if (!y || !m) return null;
  // Date.UTC rolls the year over on its own, so term 12 on a December payment is handled.
  return MONTH_YEAR_UTC_FMT.format(new Date(Date.UTC(y, m - 1 + add, 1)));
}

/* ───── styled components ───── */

// [AUTO-REFRESH 2026-08-03] See reloadGuard — blocks the auto-reload while this modal is open.
// [MOTION-A 2026-08-20] The parent keeps us mounted for the exit via usePresence. The exit is
// shorter than the entrance on purpose — dismissals should feel immediate.
// [FLUID-3 2026-10-12, user] `$open` drives the fluid recipe (components/Fluid.tsx): the shade fades in,
// fades out quicker, and stops taking clicks the moment a close begins. Was an mmFadeIn / mmFadeOut pair.
const Overlay = styled.div.attrs<{ 'data-reload-block'?: string; $open: boolean }>({ 'data-reload-block': '' })`
  position: fixed;
  inset: 0;
  /* [LIQUID-GLASS 2026-11-02, user] The shade behind the window: a little darker, a softer blur, almost no colour boost. */
  background: ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(0, 0, 0, 0.38)' : 'rgba(20, 30, 25, 0.14)'};
  backdrop-filter: blur(12px) saturate(115%);
  -webkit-backdrop-filter: blur(12px) saturate(115%);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  ${(p) => fluidScrim(p.$open)}
`;

/* [LIQUID-GLASS 2026-11-02, user] Softer frosted glass (was blur(48px) saturate(180%) over a grey gradient), 28 px
   smooth corners, a light line on the top edge and a soft reflection on the bottom one (styles/liquidGlass.ts). The
   old dark shade along the bottom is gone; the rim (::before) took its place. */
const Modal = styled.div<{ $open: boolean }>`
  position: relative;
  ${lgSurface(38)}
  ${lgSquircle(28)}
  width: 1340px;
  max-width: calc(97vw / var(--app-zoom, 1)); /* [APP-FIT 2026-10-26] the window in page px */
  max-height: calc(90vh / var(--app-zoom, 1)); /* [APP-FIT 2026-10-26] the window in page px */
  display: flex;
  flex-direction: column;
  overflow: hidden;
  /* [FLUID-3 2026-10-12, user] Rises 12px and grows from 0.95 on the spring (readable after ~0.1 s, open by
     0.36 s), closes in 0.18 s; the payment info, the mapping grid and the buttons rise in one after another.
     Replaces the mmPopIn / mmPopOut keyframes. */
  ${(p) => fluidDialog({ open: p.$open })}
  ${(p) => (p.$open ? fluidCascade('> *', 0, 20, 3) /* [FLUID-8 2026-10-12] soft: readable at once */ : '')}

  & > * { position: relative; z-index: 1; }

  /* [RANKING-ONLY 2026-10-27] phones: the WHOLE window scrolls, so the payment info scrolls away and the boxes get the
     screen; the footer with the buttons stays pinned at the bottom (ModalFooter). Before, only the body
     scrolled, under a pinned header that took more than half of a phone screen. The bottom shade is hidden: it would
     scroll up with the boxes and draw a false edge (the Inventory popup's fix, 2026-10-23). */
  @media (max-width: 768px) {
    max-height: calc(100vh - 24px);
    max-height: calc(100dvh - 24px);
    overflow-y: auto;
    overscroll-behavior: contain;
    scrollbar-width: none;
    &::-webkit-scrollbar { width: 0; height: 0; display: none; }
    /* [LIQUID-GLASS 2026-11-02] the glass rim (::before) is hidden here for the same reason the old bottom shade was:
       it would scroll up with the boxes and draw a false edge. The outer edge ring (box-shadow) stays. */
    &::before { display: none; }
  }
`;

const ModalHeader = styled.div`
  padding: 20px 24px 16px;
  border-bottom: 1px solid ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};
  background: transparent;
  border-radius: 16px 16px 0 0;
  flex-shrink: 0;

  /* [RANKING-ONLY 2026-10-27] phones: 14 px sides like the footer (was 24), so the cards get the width */
  @media (max-width: 768px) {
    padding: 16px 14px 14px;
  }
`;

// [RANKING-ONLY 2026-10-27] The title shares its line with the "Mapping to" brand pill now; the gap below the
// line is TitleRow's (was this title's own 14 px bottom margin).
const ModalTitle = styled.h2`
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: ${(p) => p.theme.colorScheme === 'dark' ? '#ffffff' : p.theme.green600};
  text-shadow: ${(p) => p.theme.colorScheme === 'dark' ? '0 1px 2px rgba(0,0,0,0.45)' : 'none'};
  letter-spacing: 0.2px;
  display: flex;
  align-items: center;
  gap: 8px;

  /* [LIQUID-GLASS 2026-11-02] A small glossy green capsule. */
  &::before {
    content: '';
    width: 4px;
    height: 18px;
    background: ${(p) => p.theme.colorScheme === 'dark'
      ? 'linear-gradient(180deg, #8ff7c6, #4acd8b)'
      : 'linear-gradient(180deg, #34d39e, #1a7f64)'};
    border-radius: 999px;
    box-shadow: 0 0 10px color-mix(in srgb, ${lgGreen} 40%, transparent);
  }
`;

// Header context fields laid out as "stat bars": each field sits on one line,
// separated by hairline vertical dividers. Both the Payment Info and Inventory Info
// rows render inside this single grid (see the comment on the component) so their
// columns share tracks and align. Values never wrap; tracks are content-sized so
// they don't clip.
const InfoGrid = styled.div`
  /* [INFO-GRID 2026-06-24] BOTH stat-bars (Payment Info + Inventory Info) render as
     ONE grid so their columns share tracks: Inventory's PM Name / Placed Value line
     up exactly under Payment's Website / Email, and each track is sized to the
     WIDEST content across both rows (minmax(max-content, 1fr) in --info-cols) so no
     label or value is ever clipped. The two rows are separated by a full-width
     <SectionDivider>; row-gap gives the breathing room. On an improbably narrow
     modal it scrolls horizontally (hidden scrollbar) rather than truncating. */
  display: grid;
  grid-template-columns: var(--info-cols);
  align-items: stretch;
  row-gap: 12px;
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar { width: 0; height: 0; display: none; }
  font-size: 11px;
  color: ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.50)' : 'rgba(22,25,23,0.72)'};
  text-transform: uppercase;
  letter-spacing: 0.6px;

  strong {
    display: block;
    color: ${(p) => p.theme.colorScheme === 'dark' ? '#ffffff' : '#161917'};
    font-weight: 700;
    font-size: 13.5px;
    text-transform: none;
    letter-spacing: 0;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* [PHONE-LAYOUT 2026-10-23] phones: the info wraps into two columns (label over value) instead of one long sideways row
     with a hidden scroll bar, which cut the website at the screen's edge; a long value (website, email) wraps in its
     column. Each heading (Payment Info / Inventory Info) takes a line of its own. Laptop unchanged. */
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    column-gap: 14px;
    row-gap: 10px;
    overflow-x: visible;

    strong {
      white-space: normal;
      overflow: visible;
      text-overflow: clip;
      overflow-wrap: anywhere;
    }
  }
`;

// One field (a single label + value) in a stat-bar row. It's a GRID CELL: its width
// comes from the shared --info-cols template, whose minmax(max-content, 1fr) tracks
// size to the widest content so full labels/values ("PAYMENT AMOUNT", "The Scale
// Agency") are NEVER clipped. Internally it stacks the label over the value. The
// leading hairline divider separates fields; the first field drops it and the last
// sheds its right padding so the bar sits flush with the header edges.
const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  box-sizing: border-box;
  padding: 0 13px;
  border-left: 1px solid ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)'};

  &:first-child { padding-left: 0; border-left: none; }
  &:last-child { padding-right: 0; }

  > span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* [PHONE-LAYOUT 2026-10-23] phones: a cell of the two-column grid (InfoGrid) — no dividers, no side padding */
  @media (max-width: 768px) {
    padding: 0;
    border-left: none;
  }
`;

// HubSpot-branded label for the Deal Owner field — HubSpot orange, tuned per
// theme so it reads clearly on both the dark and light glass header.
const HubspotLabel = styled.span`
  color: ${(p) => p.theme.colorScheme === 'dark' ? '#ff8f6b' : '#e8500f'} !important;
  font-weight: 700;
`;

// [INFO-GRID 2026-06-24] Full-width hairline between the Payment Info and Inventory
// Info rows. As a grid child spanning every column (1 / -1) it forces the Inventory
// heading onto a fresh grid row; the grid's row-gap supplies the space above/below.
const SectionDivider = styled.div`
  grid-column: 1 / -1;
  height: 1px;
  align-self: center;
  background: ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};
`;

// Heading shown as the first column of the stat-bar (e.g. "Payment Info" sitting in
// the same row as Website/Email). Fixed width + vertical-centre so the heading lines
// up across both sections and the first data field (Website / PM Name) starts at the
// same x in each row. It's a non-Field flex child, so the next <Field> becomes a
// non-first child and picks up the usual hairline divider after the heading.
const SectionHead = styled.div`
  flex: 0 0 auto;
  width: 148px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 6px;
  padding-right: 13px;
  white-space: nowrap;
  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: ${(p) => p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600};

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    flex-shrink: 0;
    background: currentColor;
    box-shadow: ${(p) => p.theme.colorScheme === 'dark' ? '0 0 6px rgba(74,205,139,0.55)' : 'none'};
  }

  /* [PHONE-LAYOUT 2026-10-23] phones: the heading on a line of its own above its fields (InfoGrid), 11 px (the smallest
     label size on a phone; was 10.5) */
  @media (max-width: 768px) {
    grid-column: 1 / -1;
    width: auto;
    padding-right: 0;
    font-size: 11px;
  }
`;

// [INV-NOTE 2026-06-24] The status line below the header — a FULL-WIDTH block (same
// length as the "Already mapped" panel) sitting just under the grid. Its top border
// IS the divider above it, matching the hairlines between the Payment and Inventory
// rows; margin/padding give it the same breathing room as the grid's row-gap. It
// reads as a plain SENTENCE (payment amount → PM / placed value → mapping status),
// not labelled sections. Only the mapping-status clause is coloured — green when
// this payment is mapped, red when it isn't — so the state is instantly scannable.
const InfoNote = styled.div`
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};
  font-size: 12.5px;
  line-height: 1.55;
  letter-spacing: 0.1px;
  color: ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.62)' : 'rgba(22,25,23,0.66)'};

  strong {
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    color: ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.92)' : '#161917'};
  }
  .ok  { font-weight: 700; color: ${(p) => p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600}; }
  .bad { font-weight: 700; color: ${(p) => p.theme.colorScheme === 'dark' ? '#f87171' : '#dc2626'}; }
`;

const ModalBody = styled.div`
  padding: 16px 24px;
  /* Only the middle scrolls — header (context) and footer (actions) stay pinned
     so the Save button is always reachable and content never feels cut off
     against the modal's bottom edge. min-height:0 lets this flex child shrink
     below its content height so the inner scroll actually engages. */
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  /* Hide the scrollbar — the content fits, so the bar only showed for a few px
     of overflow and looked out of place. Wheel/touch scroll still works as a
     fallback on very short screens. */
  scrollbar-width: none;        /* Firefox */
  -ms-overflow-style: none;     /* legacy Edge */
  &::-webkit-scrollbar { width: 0; height: 0; display: none; }

  /* [RANKING-ONLY 2026-10-27] phones: no scroll of its own, the whole window scrolls (Modal) */
  @media (max-width: 768px) {
    flex: 0 0 auto;
    min-height: auto;
    overflow: visible;
    padding: 12px 14px 14px;
  }
`;

/* ───── [RANKING-ONLY 2026-10-27, user] the "Two groups" layout ─────
   New money is mapped to The Scale Ranking only (user, 2026-10-27: "i want to remove all and i just want to use the
   scale ranking"), so the four-brand grid (a column per brand, its logo on top, a table that swiped sideways on a
   phone) became four plain cards. This is Layout 2, "Two groups", the one the user picked from five:
     Current client · PM side    Retention, Recurring Upsell, One-Time Upsell
     New client · Sales side     Recurring New Sale, One-Time New Sale
     Paid in advance (PIA)       PIA Amount, then Months paid (minus / plus), PIA for (Sales | PM), Renewal amount
     Other                       Recovery, and Shopify on a Shopify payout
   Every box but Retention and the renewal has a "Fill rest" button that puts what is still left to map (Yet to Map)
   into it. Money saved earlier on Scale Performance / Tech / Creative stays as it is, read-only, in the "Old mapping"
   line (OldMoneyStrip). */

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
`;

// "Mapping to [The Scale Ranking]" on the title line: the brand is named ONCE here instead of as four column headers.
// The logo's mono ink follows currentColor (white on dark, ink on light); its blue stays.
// [LIQUID-GLASS 2026-11-02] A glass capsule with the light top edge.
const BrandPill = styled.div`
  ${lgPill}
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  padding: 5px 14px 5px 12px;
  border-radius: 999px;
  color: ${(p) => p.theme.colorScheme === 'dark' ? '#ffffff' : '#161917'};

  > span {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    white-space: nowrap;
    color: ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.62)' : 'rgba(22,25,23,0.62)'};
  }

  /* 24 px tall (about 76 wide), so the small RANKINGS line of the logo can be read; the old column headers were 22 */
  svg {
    display: block;
    height: 24px;
    width: auto;
  }

  /* phones: the logo alone (it carries the brand name), so the title line never wraps */
  @media (max-width: 768px) {
    padding: 4px 10px;
    > span { display: none; }
    svg { height: 18px; }
  }
`;

// The money this payment already holds on a brand that is no longer mapped to: amber like the ROW flag, a padlock,
// one chip per box in the brand colours of the "Already mapped" chips. It still counts in Yet to Map and the totals,
// and Save sends it back unchanged.
const OldMoneyStrip = styled.div`
  margin-bottom: 14px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  /* [LIQUID-GLASS 2026-11-02, user] Matches the window's glass cards: rounder (16 px), a soft shine over the top half and a
     light top edge, in its own amber tint and amber hairline. */
  border-radius: 16px;
  border: 1px solid ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(251,191,36,0.40)' : 'rgba(217,119,6,0.35)'};
  background-color: ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(251,191,36,0.08)' : 'rgba(245,158,11,0.08)'};
  background-image: linear-gradient(180deg, rgba(255,255,255,${(p) => (p.theme.colorScheme === 'dark' ? 0.07 : 0.55)}), rgba(255,255,255,0) 55%);
  box-shadow: inset 0 1px 0 ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.8)'};
  color: ${(p) => p.theme.colorScheme === 'dark' ? '#fcd34d' : '#b45309'};

  .what { flex: 1; min-width: 0; }
  .what strong { display: block; font-size: 13px; font-weight: 700; }
  .what span {
    display: block;
    margin-top: 2px;
    font-size: 12px;
    line-height: 1.45;
    color: ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.72)' : 'rgba(22,25,23,0.72)'};
  }

  /* phones: the chips take a line of their own under the words */
  @media (max-width: 768px) {
    flex-wrap: wrap;
    align-items: flex-start;
    padding: 12px;
  }
`;

const OldChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;

  @media (max-width: 768px) {
    flex-basis: 100%;
    justify-content: flex-start;
  }
`;

// The four cards: two a row on a laptop (Current client | New client, then PIA | Other), one a row on a phone.
const GroupGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;

  @media (max-width: 768px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 12px;
  }
`;

// $top: keeps its own height when the card beside it grows (Other, next to an open PIA panel).
// [LIQUID-GLASS 2026-11-02, user] A glass card on the window: see-through, light top edge, smooth 20 px corners.
const GroupCard = styled.section<{ $top?: boolean }>`
  position: relative;
  ${lgCard}
  ${lgSquircle(20)}
  min-width: 0;
  align-self: ${(p) => (p.$top ? 'start' : 'stretch')};
  padding: 14px 16px;

  @media (max-width: 768px) {
    padding: 12px;
  }
`;

const GroupHead = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  h3 {
    margin: 0;
    font-size: 13.5px;
    font-weight: 700;
    color: ${(p) => p.theme.colorScheme === 'dark' ? '#ffffff' : '#161917'};
  }

  @media (max-width: 768px) {
    h3 { font-size: 14px; }
  }
`;

// Whose money it is: PM side (the app's green) or Sales side (blue).
// [LIQUID-GLASS 2026-11-02] A small glossy capsule (top sheen + a thin ring in its own colour).
const SideBadge = styled.span<{ $side: 'pm' | 'sales' }>`
  font-size: 11px;
  font-weight: 700;
  padding: 3px 9px;
  border-radius: 999px;
  white-space: nowrap;
  background-image: linear-gradient(180deg, rgba(255,255,255,0.22), rgba(255,255,255,0) 60%);
  box-shadow: inset 0 0 0 1px color-mix(in srgb, currentColor 32%, transparent), inset 0 1px 0 rgba(255,255,255,0.3);
  color: ${(p) => (p.$side === 'pm'
    ? p.theme.greenInk
    : p.theme.colorScheme === 'dark' ? '#93c5fd' : '#1d4ed8')};
  background: ${(p) => (p.$side === 'pm'
    ? p.theme.greenTint
    : p.theme.colorScheme === 'dark' ? 'rgba(96,165,250,0.14)' : 'rgba(37,99,235,0.10)')};
`;

const GroupNote = styled.div`
  margin: 3px 0 8px;
  font-size: 12px;
  line-height: 1.4;
  color: ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.62)' : 'rgba(22,25,23,0.62)'};
`;

// One line of a card: the name with a plain-words hint under it, the box, then "Fill rest". The box always sits in
// the middle column, so a line without the button (Retention) still lines up. Phones: name | box, and the button
// under the box.
const MapRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 200px 84px;
  align-items: center;
  column-gap: 12px;
  padding: 7px 0;
  border-top: 1px solid ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'};

  > :nth-child(2) { grid-column: 2; }
  > :nth-child(3) { grid-column: 3; justify-self: end; }

  @media (max-width: 768px) {
    grid-template-columns: minmax(0, 1fr) 140px;
    column-gap: 10px;
    row-gap: 6px;
    padding: 10px 0;
    > :nth-child(3) { grid-column: 2; }
  }
`;

// (Not the ROW checkbox's old RowLabel — see the ROW-AUTO note by ModalFooter.)
const LineLabel = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.90)' : '#161917'};

  @media (max-width: 768px) {
    font-size: 13.5px;
  }
`;

const LineHint = styled.div`
  margin-top: 1px;
  font-size: 11.5px;
  line-height: 1.35;
  color: ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.62)' : 'rgba(22,25,23,0.62)'};
`;

// "Fill rest": a small green pill. Greyed out when nothing is left to map.
// [LIQUID-GLASS 2026-11-02, user] A green-tinted glass capsule that squishes and glows where it is pressed.
const FillBtn = styled.button.attrs<LgPressProps>(LG_PRESS)`
  position: relative;
  overflow: hidden;
  isolation: isolate;
  height: 30px;
  padding: 0 13px;
  border-radius: 999px;
  border: none;
  background-color: color-mix(in srgb, ${lgGreen} 14%, transparent);
  background-image: linear-gradient(180deg, rgba(255,255,255,0.2), rgba(255,255,255,0) 60%);
  box-shadow: inset 0 0 0 1px color-mix(in srgb, ${lgGreen} 38%, transparent), inset 0 1px 0 rgba(255,255,255,0.25);
  color: ${(p) => p.theme.greenInk};
  font-family: inherit;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
  cursor: pointer;
  ${lgPress}

  &:hover:not(:disabled) {
    background-color: color-mix(in srgb, ${lgGreen} 20%, transparent);
  }

  &:disabled {
    opacity: 0.35;
    cursor: default;
  }

  /* phones: a 36 px tap target */
  @media (max-width: 768px) {
    height: 36px;
    padding: 0 14px;
    font-size: 12.5px;
  }
`;

// The Retention box, with a padlock + "Auto" inside it when it fills by itself (see canEditRetention).
const RetentionBox = styled.div`
  position: relative;
  min-width: 0;
`;

const AutoTag = styled.span`
  position: absolute;
  left: 10px;
  top: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  font-weight: 700;
  pointer-events: none;
  color: ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.55)' : 'rgba(22,25,23,0.55)'};

  /* phones: the padlock alone, so a large amount never runs into the word */
  @media (max-width: 768px) {
    > span { display: none; }
  }
`;

// What a PIA needs besides its amount, shown once an amount is typed (hasPiaAmount).
const PiaPanel = styled.div`
  margin-top: 4px;
  padding: 10px 12px;
  border-radius: 14px; /* [LIQUID-GLASS 2026-11-02] rounder, with a light top edge */
  background: ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(62,207,142,0.06)' : 'rgba(41,163,131,0.07)'};
  box-shadow: inset 0 1px 0 ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.7)'};
  display: flex;
  flex-direction: column;
  gap: 10px;

  @media (max-width: 768px) {
    padding: 10px;
  }
`;

// One line of the PIA panel: its name on the left, the control on the right.
const PiaLine = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  > span {
    font-size: 12.5px;
    font-weight: 600;
    color: ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.90)' : '#161917'};
  }

  @media (max-width: 768px) {
    > span { font-size: 13.5px; }
  }
`;

const Stepper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

// Minus / plus: the app's secondary button, square.
// [LIQUID-GLASS 2026-11-02] Now round glass buttons (squish + press light), like the windows' close buttons.
const StepBtn = styled.button.attrs<LgPressProps>(LG_PRESS)`
  ${lgButtonSecondary}
  width: 34px;
  padding: 0;
  font-size: 16px;
  border-radius: 50%;

  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
    font-size: 18px;
  }
`;

const StepValue = styled.span`
  min-width: 86px;
  text-align: center;
  font-size: 13.5px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: ${(p) => p.theme.colorScheme === 'dark' ? '#ffffff' : '#161917'};

  @media (max-width: 768px) {
    min-width: 76px;
    font-size: 14px;
  }
`;

// "PIA for": the app's Sales | PM switch (the Dialing Report one): a green thumb slides under the picked half and is
// hidden while neither is picked.
// [LIQUID-GLASS 2026-11-02] The track is pressed into the glass; the green thumb is glossy (shine + light top edge).
const Segmented = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: 1fr 1fr;
  ${pillTrack}
  background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(0,0,0,0.30)' : 'rgba(255,255,255,0.66)')};
  border: 1px solid transparent;
  box-shadow: ${(p) => (p.theme.colorScheme === 'dark'
    ? 'inset 0 1px 2px rgba(0,0,0,0.5), inset 0 -1px 0 rgba(255,255,255,0.07)'
    : 'inset 0 1px 2px rgba(22,25,23,0.14), inset 0 0 0 1px rgba(22,25,23,0.07)')};
`;

const SegThumb = styled.div<{ $at: 'none' | 'left' | 'right' }>`
  position: absolute;
  top: 3px;
  bottom: 3px;
  left: 3px;
  width: calc((100% - 6px) / 2);
  border-radius: 999px;
  background: linear-gradient(180deg, rgba(255,255,255,0.32), rgba(255,255,255,0) 55%),
    linear-gradient(180deg, ${(p) => p.theme.green600}, ${(p) => p.theme.green700});
  box-shadow: 0 2px 8px ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(11,102,64,0.32)' : 'rgba(20, 107, 84, 0.32)')},
    inset 0 1px 0 rgba(255,255,255,0.45);
  opacity: ${(p) => (p.$at === 'none' ? 0 : 1)};
  transform: translateX(${(p) => (p.$at === 'right' ? '100%' : '0')});
  transition: transform 0.26s cubic-bezier(0.32, 0.72, 0, 1), opacity 0.16s cubic-bezier(0.4, 0, 0.2, 1);
`;

const Seg = styled.button<{ $active: boolean }>`
  position: relative;
  z-index: 1;
  ${pillSegment}
  border: none;
  background: transparent;
  cursor: pointer;
  color: ${(p) => (p.$active ? '#fff' : p.theme.muted)};
  transition: color 180ms cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    color: ${(p) => (p.$active ? '#fff' : p.theme.colorScheme === 'dark' ? '#fff' : p.theme.text)};
  }

  /* phones: a taller tap target */
  @media (max-width: 768px) {
    padding: 9px 22px;
  }
`;

// The renewal amount inside the PIA panel: name + hint on the left, the box on the right.
const RenewRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 200px;
  align-items: center;
  column-gap: 12px;

  @media (max-width: 768px) {
    grid-template-columns: minmax(0, 1fr) 140px;
    column-gap: 10px;
  }
`;

// The plain-words line at the foot of the PIA panel: what the months cover and the month the next inventory row
// lands in. Grey for a Sales PIA, which makes no inventory row.
const PiaNote = styled.div<{ $muted?: boolean }>`
  font-size: 12px;
  font-weight: 600;
  line-height: 1.4;
  color: ${(p) => (p.$muted
    ? (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.62)' : 'rgba(22,25,23,0.62)')
    : p.theme.greenInk)};
`;

// [RANKING-ONLY 2026-10-27, user] The "Mapped $X of $Y … left to map" bar under the cards (and in the phone footer) is
// gone: "remove this thing, no need of this". Yet to Map in the header still says what is left.

const Input = styled.input`
  width: 100%;
  /* Sizing/centring lives on <MoneyWrap>. The "$" is part of the (right-aligned)
     value text, so it sits glued to the number at the right edge, e.g. "$1200". */
  display: block;
  height: 34px;
  border-radius: 12px;
  padding: 0 10px;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.3;
  /* [LIQUID-GLASS 2026-11-02, user] Pressed into the glass; the box you type in gets a soft green ring and glow. */
  ${lgSunken}
  color: ${(p) => p.theme.colorScheme === 'dark' ? '#ffffff' : p.theme.text};
  text-align: right;
  box-sizing: border-box;
  transition: border-color 120ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 120ms cubic-bezier(0.4, 0, 0.2, 1), background 120ms cubic-bezier(0.4, 0, 0.2, 1);
  -webkit-font-smoothing: antialiased;
  font-variant-numeric: tabular-nums;

  &::placeholder {
    color: ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.20)' : 'rgba(22,25,23,0.25)'};
  }

  /* Hide number spinners */
  -moz-appearance: textfield;
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &:disabled {
    /* Looks IDENTICAL to the other (editable) cells — no fade — so the Retention
       row reads like every other row; only the 🚫 cursor + tooltip signal that
       it's restricted. pointer-events lets clicks fall through to the wrapper so
       its not-allowed cursor shows and the field can never be clicked/selected. */
    cursor: not-allowed;
    pointer-events: none;
    user-select: none;
  }

  /* [RANKING-ONLY 2026-10-27] phones: 40 px tall with 16 px text (a phone zooms the page in when a box with smaller
     text is tapped) */
  @media (max-width: 768px) {
    height: 40px;
    font-size: 16px;
    border-radius: 12px; /* [LIQUID-GLASS 2026-11-02] same corners as the laptop box */
  }
`;

// Money input that shows a "$" glued to the (right-aligned) value, e.g. "$1200" —
// the "$" is part of the input's displayed text, so it sits right before the
// number at the right edge. An EMPTY cell shows nothing (no "$"). The stored value
// stays a plain numeric string (the "$" and any stray chars are stripped on
// change), so all the existing parseFloat math is unchanged.
const MoneyWrap = styled.div<{ $disabled?: boolean }>`
  display: block;
  width: 100%;
  max-width: 200px;
  margin: 0 auto;
  /* Restricted cell: the disabled input below it has pointer-events:none, so the
     hover lands HERE — show the 🚫 cursor across the whole cell area. */
  ${(p) => (p.$disabled ? 'cursor: not-allowed;' : '')}
`;

const MoneyInput: React.FC<{
  value: string;
  disabled?: boolean;
  /* [PIA-DEAL 2026-09-09] Hover text — used to say WHY a box is locked (the PIA columns on
     every department but Ranking). On the wrapper, not the input: a disabled input does not
     fire the hover events a native tooltip needs. */
  title?: string;
  onChange: (value: string) => void;
  /* [INV-SPLIT 2026-10-07] On a split website the Retention boxes ask "which PM?" when focus leaves
     them. `retention` marks a box as one of them, so moving between them does not ask. */
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  retention?: boolean;
  /* [SPLIT-RET-BY-PM 2026-10-08, user] A box that is filled through a popup instead of typing: a click
     (or Enter / Space on it) opens the popup, and the box itself only shows the result. */
  onOpen?: () => void;
  id?: string; // [RANKING-ONLY 2026-10-27] so the line's name (a <label htmlFor>) names the box
}> = ({ value, disabled, title, onChange, onBlur, retention, onOpen, id }) => {
  const ref = useRef<HTMLInputElement>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Keep only digits + a single decimal point (drops the "$" prefix, commas,
    // letters, sign). The stored value is the plain number.
    let raw = e.target.value.replace(/[^0-9.]/g, '');
    const dot = raw.indexOf('.');
    if (dot !== -1) raw = raw.slice(0, dot + 1) + raw.slice(dot + 1).replace(/\./g, '');
    onChange(raw);
    // The "$" prefix appears the moment the first digit is typed, which would
    // otherwise shove the caret left — pin it to the end after the reformat.
    requestAnimationFrame(() => {
      const el = ref.current;
      if (!el) return;
      const len = el.value.length;
      el.setSelectionRange(len, len);
      // Chrome scrolls a right-aligned input left when the caret moves, which
      // clips the value's right edge — force the right end back into view.
      el.scrollLeft = el.scrollWidth;
    });
  };

  return (
    <MoneyWrap $disabled={disabled} title={title}>
      <Input
        ref={ref}
        id={id}
        type="text"
        inputMode="decimal"
        autoComplete="off"
        value={value === '' ? '' : `$${value}`}
        disabled={disabled}
        readOnly={disabled || !!onOpen}
        tabIndex={disabled ? -1 : undefined}
        onChange={handleChange}
        onClick={onOpen}
        onKeyDown={onOpen ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(); } } : undefined}
        style={onOpen ? { cursor: 'pointer' } : undefined}
        data-retention-input={retention ? '1' : undefined}
        // Same right-align clip guard once the field loses focus.
        onBlur={(e) => { e.currentTarget.scrollLeft = e.currentTarget.scrollWidth; onBlur?.(e); }}
        placeholder=""
      />
    </MoneyWrap>
  );
};

/* [RANKING-ONLY 2026-10-27] RecoveryCell (one box centred across the four brand columns), PiaHint (the "Inventory: Dec
   2026" line under each PIA Month dropdown) and Select (those dropdowns, a restyled GlassSelect) went with the
   four-brand grid. Their jobs: MapRow, PiaNote, and the Months paid minus / plus + the Sales | PM switch. */

// [RANKING-ONLY 2026-10-27] A small padlock (inside the "Auto" Retention box, and on the "Old mapping" line). Its
// stroke follows currentColor.
const LockIcon: React.FC<{ size?: number }> = ({ size = 12 }) => (
  <svg aria-hidden="true" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <rect x="5" y="11" width="14" height="10" rx="2" />
    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
  </svg>
);

const ModalFooter = styled.div`
  display: flex;
  align-items: center;
  /* [ROW-AUTO 2026-08-07] Was "center", which only ever rendered as right-aligned
     because the ROW label sat first with margin-right:auto and absorbed the free
     space. That label is gone, so "center" would now genuinely centre the buttons
     and shift them. flex-end keeps them exactly where they have always appeared. */
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 24px;
  border-top: 1px solid ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};
  background: transparent;
  border-radius: 0 0 16px 16px;
  flex-shrink: 0;

  /* [PHONE-LAYOUT 2026-10-23] phones: two equal buttons a line (Delete Payment | Clear, then Cancel | Save) — in one line
     the four did not fit and "Delete Payment" was cut off the screen's left edge. Without Delete (not an admin) Save
     takes the second line. */
  @media (max-width: 768px) {
    flex-wrap: wrap;
    padding: 12px 14px;
    & > button { flex: 1 1 calc(50% - 5px); min-width: 0; }

    /* [RANKING-ONLY 2026-10-27] pinned to the bottom of the scrolling window (Modal). The boxes scroll under it, so its
       background is SOLID, in the window's own tone: a see-through frosted one (0.94 + blur) still let the card text
       show through the buttons in Chrome (checked on the test page). */
    && { position: sticky; bottom: 0; z-index: 2; }
    background: ${(p) => p.theme.colorScheme === 'dark' ? 'rgb(40,40,40)' : 'rgb(233,236,234)'};
  }
`;

/* [ROW-AUTO 2026-08-07] `RowLabel` (the ROW checkbox's label) removed with the
   checkbox itself — nothing renders it any more. `RowFlag` below is unrelated and
   still in use: it badges a SIBLING payment that carries the flag. */

// [BUTTON-STYLE 2026-09-30] save = the green primary, delete = the danger style, Clear and Cancel =
// the secondary. Clear only wipes the form (nothing is stored until Save), and it must not look like
// the red "Delete Payment" beside it (PAYMENT-DELETE 2026-07-17), so it is no longer red.
// min-width stays (layout).
// [LIQUID-GLASS 2026-11-02, user] Glass capsules (squish + press light): Save glossy green, Delete red-tinted.
const Btn = styled.button.attrs<LgPressProps>(LG_PRESS)<{ $variant?: 'clear' | 'save' | 'delete' }>`
  ${(p) => (p.$variant === 'save' ? lgButtonPrimary : p.$variant === 'delete' ? lgButtonDanger : lgButtonSecondary)}
  min-width: 92px;
`;

// [RANKING-ONLY 2026-10-27, user's pick] "Delete Payment" on the far left of the footer, away from Save (it sat right
// next to Clear). Phones keep their two-a-line grid (Delete Payment | Clear, then Cancel | Save).
const DeleteBtn = styled(Btn)`
  margin-right: auto;

  @media (max-width: 768px) {
    margin-right: 0;
  }
`;

// Yet-to-Map value. The `&&` raises specificity so this beats InfoGrid's
// "strong { color }" descendant rule (which otherwise wins and forces normal ink).
// Red whenever there's still an amount to map OR an over-mapped negative; at exactly
// $0.00 no colour is set, so it falls back to the normal value ink.
const RemainingBadge = styled.strong<{ $alert: boolean }>`
  && {
    font-size: 14px;
    ${(p) => (p.$alert ? 'color: #ef4444;' : '')}
  }
`;

// "Last updated by …" — a small, right-aligned line that sits at the foot of the
// scrolling body (below the Recovery row, above the footer's divider) naming the
// person who last changed this payment's mapping or value. Quiet by design: it's
// reference info, not an action.
const LastUpdated = styled.div`
  margin-top: 12px;
  text-align: right;
  font-size: 11.5px;
  font-style: italic;
  line-height: 1.4;
  color: ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.45)' : 'rgba(22,25,23,0.50)'};

  strong {
    font-weight: 700;
    font-style: normal;
    color: ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.78)' : 'rgba(22,25,23,0.78)'};
  }
  .when { opacity: 0.8; }
`;

// [FLUID-3 2026-10-12, user] The two confirm boxes over Edit Mapping (save less than the amount, delete) used to
// appear and vanish in one frame; they now open and close like every window (components/Fluid.tsx), held
// mounted through the close by usePresence below.
const ConfirmOverlay = styled.div<{ $open: boolean }>`
  position: fixed;
  inset: 0;
  /* Deeper dim + a calmer blur (no saturation boost) so the modal's bright
     labels/logos recede into an even frosted backdrop instead of bleeding
     through as glowing halos behind the dialog. */
  background: ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(10, 10, 10, 0.62)' : 'rgba(22, 25, 23, 0.42)'};
  /* [LIQUID-GLASS 2026-11-02] 3px, the user's rule for a popup over a window (10px made the text behind glow) — the
     "Which PM?" popup's value (popup-backdrop-light-blur). */
  backdrop-filter: blur(3px);
  -webkit-backdrop-filter: blur(3px);
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: center;
  ${(p) => fluidScrim(p.$open)}
`;

const ConfirmBox = styled.div<{ $open: boolean }>`
  ${(p) => fluidDialog({ open: p.$open })}
  ${(p) => (p.$open ? fluidCascade('> *', 0, 20, 2) /* [FLUID-8 2026-10-12] soft: readable at once */ : '')}
  position: relative;
  /* [LIQUID-GLASS 2026-11-02, user] The frosted glass of the window behind it (light top edge, 24 px smooth corners). */
  ${lgSurface(38)}
  ${lgSquircle(24)}
  padding: 24px 28px;
  max-width: 420px;
  text-align: center;
  & > * { position: relative; z-index: 1; }
`;

const ConfirmMsg = styled.p`
  font-size: 14px;
  font-weight: 600;
  color: ${(p) => p.theme.colorScheme === 'dark' ? '#ffffff' : p.theme.text};
  text-shadow: ${(p) => p.theme.colorScheme === 'dark' ? '0 1px 2px rgba(0,0,0,0.30)' : 'none'};
  margin: 0 0 20px;
  line-height: 1.5;
`;

const ConfirmActions = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
`;

/* ───── prior-mapping reference panel ───── */

// Same department/recovery palette as the Payments table tag chips, so the
// "already mapped this month" breakdown reads identically to the row tags.
const CHIP_PALETTE: Record<string, { bar: string; rgb: string; valueDark: string; valueLight: string }> = {
  ranking:     { bar: '#13b790', rgb: '19,183,144',  valueDark: '#84e8af', valueLight: '#0f766e' },
  performance: { bar: '#6f7472', rgb: '111,116,114', valueDark: '#d4d4d4', valueLight: '#3d4140' },
  tech:        { bar: '#8b5cf6', rgb: '139,92,246',  valueDark: '#c4b5fd', valueLight: '#6d28d9' },
  creative:    { bar: '#ec4899', rgb: '236,72,153',  valueDark: '#f9a8d4', valueLight: '#be185d' },
  recovery:    { bar: '#0ea5e9', rgb: '14,165,233',  valueDark: '#7dd3fc', valueLight: '#0369a1' },
  shopify:     { bar: '#95bf47', rgb: '149,191,71',  valueDark: '#c5e3a0', valueLight: '#4d7c0f' }, // [SHOPIFY 2026-10-19]
};

/* [LIQUID-GLASS 2026-11-02, user] The glossy capsule finish for the small badges and chips ("Already mapped", ROW, the
   category chips): a soft shine over the top half and a light top edge, over each one's own tint. */
const GLOSS = (p: { theme: { colorScheme: string } }) => `
  background-image: linear-gradient(180deg, rgba(255,255,255,${p.theme.colorScheme === 'dark' ? 0.14 : 0.45}), rgba(255,255,255,0) 60%);
  box-shadow: inset 0 1px 0 rgba(255,255,255,${p.theme.colorScheme === 'dark' ? 0.16 : 0.6});
`;

/* [INV-SPLIT 2026-10-07] The "Split between PMs" line above the grid — same quiet panel chrome as the
   reference panel below it, so it reads as information, not an action. */
const SplitPanel = styled.div`
  margin-bottom: 14px;
  border-radius: 14px; /* [LIQUID-GLASS 2026-11-02] rounder, light top edge */
  box-shadow: inset 0 1px 0 ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.7)'};
  padding: 10px 14px;
  display: grid;
  gap: 4px;
  font-size: 13px;
  line-height: 1.5;
  font-variant-numeric: tabular-nums;
  border: 1px solid ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(74,205,139,0.28)' : 'rgba(22,115,90,0.22)'};
  background: ${(p) => p.theme.greenTint};
  strong { color: ${(p) => p.theme.greenInk}; }
`;

// [LIQUID-GLASS 2026-11-02, user] The "Already mapped" panel is a glass card, like the four group cards below it.
const RefPanel = styled.div`
  position: relative;
  ${lgCard}
  ${lgSquircle(16)}
  margin-bottom: 14px;
  padding: 12px 14px;
`;

// Compact green "Already mapped" pill that leads each reference row, replacing
// the old full-width panel title — so the label, date, website, amount and the
// mapped-category chip all live on one line.
const RefBadge = styled.span<{ $tone?: 'mapped' | 'refunded' }>`
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
  padding: 2px 8px;
  border-radius: 999px;
  /* [REF-REFUNDED 2026-08-21] Red for a sibling that was refunded/disputed in full with
     nothing mapped - the same red the table's Refund/Dispute column uses, so it reads as the
     same fact. Green stays the genuinely-mapped colour. */
  border: 1px solid ${(p) => (p.$tone === 'refunded'
    ? (p.theme.colorScheme === 'dark' ? 'rgba(248,113,113,0.50)' : 'rgba(220,38,38,0.40)')
    : (p.theme.colorScheme === 'dark' ? 'rgba(74,205,139,0.45)' : 'rgba(26,159,125,0.40)'))};
  color: ${(p) => (p.$tone === 'refunded'
    ? (p.theme.colorScheme === 'dark' ? '#f87171' : '#dc2626')
    : (p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600))};
  background: ${(p) => (p.$tone === 'refunded'
    ? (p.theme.colorScheme === 'dark' ? 'rgba(248,113,113,0.10)' : 'rgba(220,38,38,0.07)')
    : (p.theme.colorScheme === 'dark' ? 'rgba(74,205,139,0.10)' : 'rgba(26,159,125,0.08)'))};
  ${GLOSS} /* [LIQUID-GLASS 2026-11-02] */
`;

/* [REF-REFUNDED 2026-08-21] What a sibling row in the reference strip actually represents.

   WHY THIS EXISTS. A sibling reaches this strip because its mapped_status is TRUE - but that
   column is GENERATED as (total_amount = final_amount), and a payment refunded in full has
   final_amount 0 with nothing mapped, so 0 = 0 reads as "mapped". It then showed up here under
   a green "Already mapped" badge with "No category breakdown" - telling the person mapping the
   NEXT payment that prior money had been placed when in fact it had all gone back. Measured on
   2026-08-21: 145 such rows in 2026 ($92k). The strip is the one place the next payment's
   mapper learns the site's history, so the truthful label matters more here than anywhere.

   A fully refunded/disputed sibling that still CARRIES a mapping never gets here: its
   total_amount stays > 0 while final_amount drops to 0, so mapped_status flips FALSE and the
   row is simply not a sibling. Hence "refunded" here always means "and nothing was mapped". */
function classifyReference(sib: Payment): {
  kind: 'mapped' | 'refunded' | 'disputed';
  lostAmount: number;   // refund + dispute against this payment
  partial: boolean;     // some money came back but the payment still carries a mapping
} {
  const amount = Number(sib.amount) || 0;
  // `refunded` / `dispute_amount` are the per-row figures the payments API computes (same
  // month, capped at the payment amount) - the same numbers the table's Refund/Dispute column
  // shows, so this badge can never disagree with that column.
  const refunded = Number((sib as any).refunded) || 0;
  const disputed = Number((sib as any).dispute_amount) || 0;
  const lost = refunded + disputed;
  // "Nothing mapped" is derived from the mapping columns the row actually carries (the same
  // aggregator the category chips use) - NOT from total_amount, which the payments API does
  // not send to the client. Reading an absent field would have made every row look unmapped.
  let mappedSum = 0;
  for (const v of aggregateByCategory(sib as any).values()) mappedSum += v;
  const nothingMapped = mappedSum <= 0.005;
  if (lost > 0 && nothingMapped && lost >= amount - 0.005) {
    return { kind: disputed > refunded ? 'disputed' : 'refunded', lostAmount: lost, partial: false };
  }
  return { kind: 'mapped', lostAmount: lost, partial: lost > 0 };
}

// All prior mapped payments in the bucket now live on ONE line, separated by a
// "|" divider (see <RefDivider>) and ordered by payment date (earliest first).
// RefRow is the single flex line; it wraps only as a last resort on a very narrow
// modal. Holds the shared inline text styling (.sep/.amt/.site) for its entries.
const RefRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px 14px;
  font-size: 12px;
  color: ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.70)' : 'rgba(22,25,23,0.70)'};

  .amt {
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    color: ${(p) => p.theme.colorScheme === 'dark' ? '#ffffff' : '#161917'};
  }
  .site {
    font-weight: 600;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    color: ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.85)' : 'rgba(22,25,23,0.85)'};
  }
  .sep { opacity: 0.4; }

  /* [RANKING-ONLY 2026-10-27] phones: each earlier payment on lines of its own, left-aligned, wrapping inside the
     window (a long website breaks). The whole window scrolls on a phone now and never sideways, so a one-line entry
     (up to about 520 px) would be cut at the edge; before, the body scrolled it sideways behind a hidden scroll bar. */
  @media (max-width: 768px) {
    justify-content: flex-start;
    .site { overflow: visible; overflow-wrap: anywhere; }
  }
`;

// One prior payment's pieces ([Already mapped] · date · site · amount · chips)
// kept together as a single non-wrapping unit so a "|"-separated entry never
// splits mid-way across the row.
const RefEntry = styled.div`
  display: inline-flex;
  align-items: center;
  flex-wrap: nowrap;
  white-space: nowrap;
  gap: 8px;

  /* [RANKING-ONLY 2026-10-27] phones: a full line per entry that may wrap (see RefRow) */
  @media (max-width: 768px) {
    flex: 1 1 100%;
    min-width: 0;
    flex-wrap: wrap;
    white-space: normal;
    row-gap: 6px;
  }
`;

// The "|" between two consecutive references — quiet, vertically centred.
const RefDivider = styled.span`
  font-weight: 400;
  opacity: 0.35;
  user-select: none;
  color: ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.55)' : 'rgba(22,25,23,0.55)'};

  /* [RANKING-ONLY 2026-10-27] phones: not needed, each entry has lines of its own (RefEntry) */
  @media (max-width: 768px) {
    display: none;
  }
`;

const RowFlag = styled.span`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.4px;
  padding: 2px 7px;
  border-radius: 999px;
  border: 1px solid ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(251,191,36,0.50)' : 'rgba(217,119,6,0.45)'};
  color: ${(p) => p.theme.colorScheme === 'dark' ? '#fcd34d' : '#b45309'};
  background: ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(251,191,36,0.12)' : 'rgba(245,158,11,0.10)'};
  ${GLOSS} /* [LIQUID-GLASS 2026-11-02] */
`;

// Keeps every mapped-category chip on a single line as one unit, so a payment
// with multiple breakdowns (e.g. Retention + One-Time Upsell) never strands a
// lone chip on its own row — the whole group either sits inline or wraps
// together, centered, which reads as intentional.
const ChipGroup = styled.span`
  display: inline-flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 8px;

  /* [RANKING-ONLY 2026-10-27] phones: the chips may wrap inside the window (see RefRow) */
  @media (max-width: 768px) {
    flex-wrap: wrap;
  }
`;

const Chip = styled.span<{ dept: string }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border-radius: 999px;
  line-height: 1;
  padding: 4px 10px;
  ${(p) => {
    const pal = CHIP_PALETTE[p.dept] ?? CHIP_PALETTE.performance;
    const isDark = p.theme.colorScheme === 'dark';
    return isDark
      ? `border: 1px solid rgba(${pal.rgb}, 0.45); background: rgba(${pal.rgb}, 0.10);`
      : `border: 1px solid rgba(${pal.rgb}, 0.38); background: rgba(${pal.rgb}, 0.08);`;
  }}
  ${GLOSS} /* [LIQUID-GLASS 2026-11-02] */
`;

const ChipDot = styled.span<{ dept: string }>`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
  ${(p) => {
    const pal = CHIP_PALETTE[p.dept] ?? CHIP_PALETTE.performance;
    return `background: ${pal.bar}; box-shadow: 0 0 0 2px rgba(${pal.rgb}, 0.20);`;
  }}
`;

const ChipLabel = styled.span`
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.2px;
  white-space: nowrap;
  color: ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.55)' : 'rgba(22,25,23,0.60)'};
`;

const ChipValue = styled.span<{ dept: string }>`
  font-size: 11.5px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  color: ${(p) => {
    const pal = CHIP_PALETTE[p.dept];
    if (!pal) return p.theme.text;
    return p.theme.colorScheme === 'dark' ? pal.valueDark : pal.valueLight;
  }};
`;

/* ───── constants ───── */

/* [RANKING-ONLY 2026-10-27] All four brands stay in DEPTS: payments mapped before the change still hold Performance /
   Tech / Creative money (42 payments, all in 2026, $41,034 on 2026-10-27; sale and upsell boxes only), and it still
   counts in Yet to Map, the totals, the status sentence, the "Already mapped" chips and the deal popup. Only the BOXES
   are Scale Ranking only now (RANKING, OLD_DEPTS below). */
const DEPTS = ['ranking', 'performance', 'tech', 'creative'] as const;
const DEPT_LABELS: Record<string, string> = {
  ranking: 'Ranking',
  performance: 'Performance',
  tech: 'Tech',
  creative: 'Creative',
};

interface RowDef {
  label: string;
  suffix: string;
  type: 'money' | 'month' | 'dept';
}

const ROWS: RowDef[] = [
  { label: 'Retention', suffix: 'retention', type: 'money' },
  { label: 'Recurring Upsell', suffix: 'r_so', type: 'money' },
  { label: 'One-Time Upsell', suffix: 'o_so', type: 'money' },
  { label: 'Recurring New Sale', suffix: 'r_new_sale', type: 'money' },
  { label: 'One-Time New Sale', suffix: 'o_new_sale', type: 'money' },
  { label: 'PIA Amount', suffix: 'pia_amount', type: 'money' },
  { label: 'PIA Month', suffix: 'pia_month', type: 'month' },
  { label: 'PIA Dept', suffix: 'pia_dept', type: 'dept' },
];

const MONETARY_SUFFIXES = ['retention', 'r_new_sale', 'o_new_sale', 'r_so', 'o_so', 'pia_amount'];

/* [PIA-DEAL 2026-09-09] PIA is recorded on the RANKING department ONLY. This is how it has always
   worked in practice — every PIA on record is Ranking, and creative/performance/tech PIA are NULL on
   all 6.4 M payments — but the grid used to let all four be typed into. It matters now that PIA
   creates a HubSpot deal: the deal's pipeline and owner list follow the department TAG
   (ranking_pia_dept), so a PIA split across departments with different tags would have no single
   correct pipeline.
   [RANKING-ONLY 2026-10-27, user] Now EVERY box is Scale Ranking only ("i just want to use the scale ranking"): the
   form has one box per type, all on Ranking. The other three brands are OLD_DEPTS — no box, read-only. Their saved
   money is shown in the "Old mapping" line and sent back UNCHANGED on Save, never as null, so a save can never wipe it
   (the server writes only the keys it is sent; the deal popup adds up all four brands from what was sent). */
const RANKING = 'ranking';
const OLD_DEPTS = DEPTS.filter((d) => d !== RANKING);
const OLD_BRAND_NAMES: Record<string, string> = {
  performance: 'Scale Performance',
  tech: 'Scale Tech',
  creative: 'Scale Creative',
};
const isOldBrandKey = (key: string) => OLD_DEPTS.some((d) => key.startsWith(`${d}_`));

// [INV-SPLIT 2026-10-07] Money to the cent (module level, so hook dependency lists stay stable).
const roundCents = (n: number) => Math.round(n * 100) / 100;

function fieldKey(dept: string, suffix: string) {
  return `${dept}_${suffix}`;
}

function fmt(n: number) {
  return `$${MONEY_FMT.format(n)}`;
}

function fmtDate(d: string | null | undefined) {
  if (!d) return '—';
  return MONTH_SHORT_DAY_YEAR_IST_FMT.format(new Date(d));
}

// Audit timestamps come back from the server in UTC ("YYYY-MM-DDTHH:mm:ss", no
// zone) — same as the Application Logs page. Append "Z" so the wall-clock reads
// as UTC, then render in IST (the team's timezone) to match that page. Returns ''
// on a missing/bad value so the caller can drop the "·" separator entirely.
function fmtWhen(d: string | null | undefined) {
  if (!d) return '';
  const utc = new Date(`${d}Z`);
  if (isNaN(utc.getTime())) return '';
  const date = MONTH_SHORT_DAY_YEAR_IST_FMT.format(utc);
  const time = TIME_IST_FMT.format(utc);
  return `${date}, ${time} IST`;
}

interface RefChipDef { key: string; dept: string; label: string; value: string; }

// Extract the non-zero mapping allocations of a prior payment into display
// chips — same set/labels as the Payments table tags (every dept × category
// plus PIA month/dept and Recovery).
function buildRefChips(sib: Payment): RefChipDef[] {
  const chips: RefChipDef[] = [];
  for (const dept of DEPTS) {
    for (const row of ROWS) {
      const k = fieldKey(dept, row.suffix);
      const v = (sib as any)[k];
      if (v == null || v === '' || (row.type === 'money' && Number(v) === 0)) continue;
      const value = row.type === 'money' ? `$${Number(v).toLocaleString()}` : String(v);
      chips.push({ key: k, dept, label: row.label, value });
    }
  }
  const rec = (sib as any).recovery;
  if (rec != null && rec !== '' && Number(rec) !== 0) {
    chips.push({ key: 'recovery', dept: 'recovery', label: 'Recovery', value: `${Number(rec).toLocaleString()}` });
  }
  const shop = (sib as any).shopify_amount; // [SHOPIFY 2026-10-19]
  if (shop != null && shop !== '' && Number(shop) !== 0) {
    chips.push({ key: 'shopify', dept: 'shopify', label: 'Shopify', value: `${Number(shop).toLocaleString()}` });
  }
  return chips;
}

// [INV-NOTE 2026-06-24] Sum a payment-like record's non-zero MONETARY allocations
// (across all four depts) into a category→total map, e.g. { Retention: 1800 }.
// Works for both a saved sibling Payment and the live editable `fields` object,
// since parseFloat handles numbers and strings alike. PIA Month/Dept (non-money)
// are skipped; Recovery is folded in as its own category.
function aggregateByCategory(src: Record<string, any>): Map<string, number> {
  const out = new Map<string, number>();
  for (const row of ROWS) {
    if (row.type !== 'money') continue;
    let sum = 0;
    for (const dept of DEPTS) {
      const v = parseFloat(String(src[fieldKey(dept, row.suffix)] ?? ''));
      if (!isNaN(v)) sum += v;
    }
    if (sum > 0) out.set(row.label, (out.get(row.label) ?? 0) + sum);
  }
  const rec = parseFloat(String(src.recovery ?? ''));
  if (!isNaN(rec) && rec > 0) out.set('Recovery', (out.get('Recovery') ?? 0) + rec);
  const shop = parseFloat(String(src.shopify_amount ?? '')); // [SHOPIFY 2026-10-19]
  if (!isNaN(shop) && shop > 0) out.set('Shopify', (out.get('Shopify') ?? 0) + shop);
  return out;
}

// Join a category map for prose: a single category → just its name ("Retention");
// several → a name + amount list ("Retention $1,000.00, Recurring Upsell $500.00"),
// largest first. Category names only (no dept) per the agreed copy.
function listCategories(m: Map<string, number>): string {
  const entries = [...m.entries()].sort((a, b) => b[1] - a[1]);
  if (entries.length === 1) return entries[0][0];
  return entries.map(([label, amt]) => `${label} ${fmt(amt)}`).join(', ');
}

// Like listCategories, but names the DEPARTMENT/column each value sits in after the
// row label, e.g. "Recurring New Sale (Tech)" (Ranking alone is left unnamed, see below) — used only for
// the live "is mapped to …" green clause so the column is named, not just the row.
// A category split across depts lists them all ("Retention (Ranking, Tech)"); Recovery
// is a single non-department field, so it gets no parenthetical.
function listCategoriesWithDept(src: Record<string, any>): string {
  const entries: { label: string; total: number; depts: string[] }[] = [];
  for (const row of ROWS) {
    if (row.type !== 'money') continue;
    let total = 0;
    const depts: string[] = [];
    for (const dept of DEPTS) {
      const v = parseFloat(String(src[fieldKey(dept, row.suffix)] ?? ''));
      if (!isNaN(v) && v > 0) { total += v; depts.push(DEPT_LABELS[dept] ?? dept); }
    }
    if (total > 0) entries.push({ label: row.label, total, depts });
  }
  const rec = parseFloat(String(src.recovery ?? ''));
  if (!isNaN(rec) && rec > 0) entries.push({ label: 'Recovery', total: rec, depts: [] });
  const shop = parseFloat(String(src.shopify_amount ?? '')); // [SHOPIFY 2026-10-19]
  if (!isNaN(shop) && shop > 0) entries.push({ label: 'Shopify', total: shop, depts: [] });

  entries.sort((a, b) => b.total - a.total);
  // [RANKING-ONLY 2026-10-27] Scale Ranking ALONE is not named any more (every box is Ranking now, so "(Ranking)" said
  // nothing); a category that also sits on an old brand still names them all, e.g. "One-Time Upsell (Ranking, Tech)".
  const withDept = (label: string, depts: string[]) =>
    depts.length && !(depts.length === 1 && depts[0] === DEPT_LABELS[RANKING])
      ? `${label} (${depts.join(', ')})`
      : label;
  if (entries.length === 1) return withDept(entries[0].label, entries[0].depts);
  return entries.map((e) => `${withDept(e.label, e.depts)} ${fmt(e.total)}`).join(', ');
}

/* ───── component ───── */

interface Props {
  payment: Payment;
  // Mapped siblings in the same (website, month) bucket — shown read-only above
  // the mapping grid so the user can replicate how prior payments were split.
  references?: Payment[];
  onClose: () => void;
  onSave: (id: number, fields: Record<string, any>) => Promise<void>;
  // [PAYMENT-DELETE 2026-07-17] Admin-only soft-delete. Passed ONLY for admins (PaymentsPage
  // gates it on is_admin); when absent the "Delete Payment" button never renders. Resolves
  // after the row is deleted; the parent then closes the modal + refreshes.
  onDelete?: (id: number) => Promise<void>;
  // [MOTION-A 2026-08-20] True while the parent holds us mounted for the exit animation
  // (usePresence). Drives the overlay/panel exit keyframes and disables interaction.
  closing?: boolean;
}

const MappingModal: React.FC<Props> = ({ payment, references = [], onClose, onSave, onDelete, closing = false }) => {
  const [fields, setFields] = useState<Record<string, any>>({});
  /* [ROW-AUTO 2026-08-07] No `rowStatus` state any more. ROW is no longer picked here:
     it is derived server-side from the deal owner's HubSpot team (see routes/hubspot.ts).
     The payment's stored `row_status` is still READ below, for display only. */
  const [saving, setSaving] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  // [PAYMENT-DELETE 2026-07-17] Delete confirmation gate + in-flight flag.
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [lastEdit, setLastEdit] = useState<PaymentLastEdit | null>(null);
  // [FLUID-3 2026-10-12] Edit Mapping is open one frame after mount (so the spring has a closed state to start
  // from) until the parent starts the close; each confirm box is held mounted through its own 0.18 s close.
  const open = usePresence(!closing, fluid.dialogOutMs).entered;
  const confirmPres = usePresence(showConfirm, fluid.dialogOutMs);
  const deletePres = usePresence(showDeleteConfirm, fluid.dialogOutMs);

  // Pull "who last changed this payment" from the audit trail when the modal
  // opens (or switches rows). Shown as the footer "Last updated by …" line. The
  // value reflects the prior edit — after this user saves and reopens the row, it
  // will name them. Best-effort: any failure just hides the line.
  useEffect(() => {
    let active = true;
    setLastEdit(null);
    fetchPaymentLastEdit(payment.id)
      .then((info) => { if (active) setLastEdit(info); })
      .catch(() => { /* non-critical — leave the line hidden */ });
    return () => { active = false; };
  }, [payment.id]);

  // Initialise from payment data
  useEffect(() => {
    const init: Record<string, any> = {};
    for (const dept of DEPTS) {
      for (const row of ROWS) {
        const k = fieldKey(dept, row.suffix);
        const val = (payment as any)[k];
        if (val == null || val === '' || (row.type === 'money' && Number(val) === 0)) {
          init[k] = '';
        } else {
          init[k] = String(val);
        }
      }
    }
    // Recovery is a single (non-department) money field, kept outside the
    // dept × suffix grid under the plain `recovery` key.
    const recVal = (payment as any).recovery;
    init.recovery = recVal == null || recVal === '' || Number(recVal) === 0 ? '' : String(recVal);
    // [PIA-RENEWAL 2026-07-10] Single (non-department) PIA renewal amount — same
    // storage shape as recovery (a plain key, not dept × suffix).
    const renewVal = (payment as any).pia_renewal_amount;
    init.pia_renewal_amount = renewVal == null || renewVal === '' || Number(renewVal) === 0 ? '' : String(renewVal);
    // [SHOPIFY 2026-10-19] Single (non-department) Shopify amount — same storage shape as recovery.
    const shopVal = (payment as any).shopify_amount;
    init.shopify_amount = shopVal == null || shopVal === '' || Number(shopVal) === 0 ? '' : String(shopVal);
    setFields(init);
  }, [payment]);

  // Effective refund = capped refund sum + dispute sum (matches the
  // Refund/Dispute column shown in the Payments table). A row whose dispute
  // fully neutralizes the payment will have remaining = 0 here even if the
  // stored payments.refunded column is 0.
  const refundedDisplay = Number(payment.refunded || 0) + Number((payment as any).dispute_amount || 0);
  const finalAmount = Math.max(0, Number(payment.amount) - refundedDisplay);
  const hasPlacedValue = payment.placed_value != null && Number(payment.placed_value) > 0;
  // [RETENTION-EDIT 2026-07-03] Retention is EDITABLE only on an inventory-backed
  // payment — one that carries BOTH a PM name and a placed value. Such a row shows the
  // server's auto-filled retention (from the CR-sheet placed value, capped by
  // autoMapRetention.ts) but a person may now override it. A payment missing either
  // piece has nothing to retain against, so retention stays fully restricted there
  // (read-only + hard-blocked) and is left entirely to the auto-allocator.
  // [INV-SPLIT 2026-10-07, user] A website split between PMs: retention is never auto-filled there, so
  // it is always typed by hand — and every amount is given to a PM through RetentionPmPopup.
  const splitParts: SplitPartInfo[] = useMemo(() => (payment as any).split_parts ?? [], [payment]);
  const isSplit = splitParts.length > 0;
  const canEditRetention = isSplit || !!(payment.pm_name && hasPlacedValue);

  // Live remaining calculation
  const totalMapped = useMemo(() => {
    let sum = 0;
    for (const dept of DEPTS) {
      for (const suf of MONETARY_SUFFIXES) {
        const v = parseFloat(fields[fieldKey(dept, suf)]);
        if (!isNaN(v)) sum += v;
      }
    }
    // Recovery counts toward the mapped total too (matches the DB total_amount).
    const rec = parseFloat(fields.recovery);
    if (!isNaN(rec)) sum += rec;
    // [SHOPIFY 2026-10-19] So does the Shopify amount (inside total_amount).
    const shop = parseFloat(fields.shopify_amount);
    if (!isNaN(shop)) sum += shop;
    return sum;
  }, [fields]);

  const remaining = finalAmount - totalMapped;
  // Yet-to-Map is flagged RED whenever it's not settled — a positive amount still
  // left to map, OR a negative amount from mapping more than the payment by mistake.
  // At exactly $0.00 (half-cent epsilon absorbs float noise) it shows normal ink.
  const remainingAlert = Math.abs(remaining) >= 0.005;

  /* [INV-SPLIT 2026-10-07, user] ── retention BY PM on a split website ─────────────────────────────
     pmShares   how THIS payment's retention is shared between the PMs (saved value first; edited here).
     pmStep     the open "Retention by PM" popup: which Retention box it fills, and whether Save opened it.
     [SPLIT-RET-BY-PM 2026-10-08, user] The popup opens AT ONCE when a Retention box is clicked — "when I
     click on retention row then the popup should come immediately with all the PM names" — and takes an
     amount against each PM (each at most that PM's open part, together at most the payment). The box
     then shows the sum. Save still opens it when the retention and the shares disagree (a payment
     saved before this), and the save carries on by itself once it is answered. */
  const savedShares: PmRetentionShare[] = useMemo(() => (payment as any).retention_pm_splits ?? [], [payment]);
  const [pmShares, setPmShares] = useState<PmRetentionShare[]>(savedShares);
  const [pmStep, setPmStep] = useState<null | { dept: string; fromSave: boolean }>(null);
  // [FLUID-3 2026-10-12] "Retention by PM" stays mounted through its 0.18 s close. pmSeq keys each opening, so one
  // opened again while the last is still closing starts fresh (its amounts come from the shares, as on any open).
  const pmPres = usePresence(pmStep, fluid.dialogOutMs);
  const [pmSeq, setPmSeq] = useState(0);
  const resumeSaveRef = useRef(false);
  useEffect(() => { setPmShares(savedShares); setPmStep(null); resumeSaveRef.current = false; }, [savedShares]);

  const retentionTotal = useMemo(
    () => roundCents(DEPTS.reduce((s, d) => s + (parseFloat(fields[fieldKey(d, 'retention')]) || 0), 0)),
    [fields],
  );
  const sharesSum = roundCents(pmShares.reduce((s, x) => s + (Number(x.amount) || 0), 0));
  const sharesMatch = Math.abs(sharesSum - retentionTotal) < 0.005;
  const sharesKey = (list: PmRetentionShare[]) =>
    [...list].filter((x) => Number(x.amount) > 0.005).map((x) => `${x.pm}=${roundCents(Number(x.amount))}`).sort().join('|');
  const sharesChanged = sharesKey(pmShares) !== sharesKey(savedShares);
  // [RANKING-ONLY 2026-10-27] `retentionDept` (the brand box the retention was typed in) is gone: there is one
  // Retention box now, Scale Ranking's. retentionTotal still adds all four, exactly as the server checks the PM shares
  // (no payment holds old-brand Retention: 0 on 2026-10-27).

  // [SPLIT-RET-BY-PM 2026-10-08, user] Opens from a click on a Retention box (dept = that box), or from
  // Save when the retention and the shares disagree (then the save carries on after the answer).
  const openPmStep = (dept: string, fromSave = false) => {
    resumeSaveRef.current = fromSave;
    setPmStep({ dept, fromSave });
    setPmSeq((n) => n + 1); // [FLUID-3 2026-10-12]
  };

  // What each PM can take on this payment: their part, less what OTHER payments already hold
  // (split_parts.mapped counts this payment's SAVED share too, so that is taken back out).
  const stepParts: PmStepPart[] = useMemo(() => splitParts.map((p) => {
    const savedHere = savedShares.filter((s) => s.pm === p.pm).reduce((t, s) => t + Number(s.amount), 0);
    const others = Math.max(0, roundCents(Number(p.mapped) - savedHere));
    return { pm: p.pm, part: Number(p.amount), others, open: roundCents(Number(p.amount) - others) };
  }), [splitParts, savedShares]);
  // What is free for retention on this payment: its amount less what it maps in the other categories.
  const stepMaxTotal = roundCents(finalAmount - (totalMapped - retentionTotal));

  const confirmPmStep = (shares: PmStepShare[]) => {
    if (!pmStep) return;
    setPmShares(shares.map((s) => ({ pm: s.pm, amount: roundCents(Number(s.amount)) })));
    // The retention IS the sum of the PM amounts, so the shares and the retention total always agree and
    // Save never has to ask again. [RANKING-ONLY 2026-10-27] It goes in the one Retention box, Scale
    // Ranking's; the old brands' Retention is never touched here (it was cleared before; it is read-only now).
    const sum = roundCents(shares.reduce((t, s) => t + (Number(s.amount) || 0), 0));
    setField(fieldKey(RANKING, 'retention'), sum > 0.005 ? String(sum) : '');
    setPmStep(null);
  };

  // [DEAL-OWNER 2026-07-28] The `retentionOnly` memo that used to live here (true when
  // Retention was the ONLY mapped value) existed solely to hide the Deal Owner line.
  // It is gone with that gate — see showDealOwner below.

  // [PIA-REVEAL 2026-06-24] The PIA Month / PIA Dept rows stay hidden until a PIA
  // Amount has been entered for SOME department. True when any department's PIA
  // Amount cell holds a positive value; the moment one does, both follow-up rows
  // reveal so Month + Dept can be completed (PIA is all-or-nothing per department,
  // enforced on save). Reads live grid values so it tracks typing/clearing.
  const hasPiaAmount = useMemo(() => {
    for (const dept of DEPTS) {
      const v = parseFloat(fields[fieldKey(dept, 'pia_amount')]);
      if (!isNaN(v) && v > 0) return true;
    }
    return false;
  }, [fields]);

  /* [RANKING-ONLY 2026-10-27] `decidingPiaTerm` (the longest PM term when two brands carried a PIA, so a shorter one's
     hint could name the month that really wins) is gone: PIA has one box now, Scale Ranking's, so its own term is the
     one that decides — the server's pmPiaTerm() reads the same single term. */

  // [DEAL-OWNER 2026-07-28] Show the owner whenever the row HOLDS a deal — the
  // `retentionOnly` gate was dropped. It read the LIVE grid, so clearing an upsell
  // cell mid-edit hid the owner of a deal that still existed, and an old row whose
  // deal predates the owner stamp looked deal-less. Deal existence is now the
  // authoritative signal: a deal that no longer backs a mapping is archived and
  // unlinked (services/hubspotDeals detachPaymentDeals), which clears deal_owner
  // and hides this line by itself.
  const showDealOwner = !!payment.deal_owner;

  /* [PIA-DEAL 2026-09-09] The deal owner(s), each said WHERE they come from:
         "Travis Smith, Amaan Sofi (PIA)"
         "Radhika Saini (Split Deals), Vishal Suryavanshi (Split Deals)"
     A payment can now hold a sale/upsell deal AND a PIA deal with different owners, and the
     bare comma-joined `deal_owner` gave no way to tell which name was which.

     Built from `deal_splits` — {slot: [{dealId, owner, ...}]} — because that is the only place
     that records which SLOT a name belongs to. A slot holding two parts is a split; the
     `pia_amount` slot is the PIA. Everything else is an ordinary sale/upsell and stays bare, so
     the common single-deal row reads exactly as it always has.

     ⛔ Display only. `payment.deal_owner` itself stays clean names — it is read as a real
     person's name by the upsell PM attribution, Campaign History's sale-owner query, the ROW
     tag, and positionally by DealCreationModal to pre-fill the owner dropdowns, where an
     unresolvable "Name (PIA)" would make the server refuse the update.

     Falls back to the raw column whenever there is no layout (deals created before
     deal_splits existed), so an old row can never lose its owner line. */
  const dealOwnerLabelled = useMemo(() => {
    const raw = String(payment.deal_owner || '').trim();
    let layout: any = (payment as any).deal_splits;
    if (typeof layout === 'string') {
      try { layout = JSON.parse(layout); } catch { layout = null; }
    }
    if (!layout || typeof layout !== 'object') return raw;

    const out: string[] = [];
    for (const [slot, parts] of Object.entries(layout) as [string, any][]) {
      const list = Array.isArray(parts) ? parts : [];
      const tag = slot === 'pia_amount' ? ' (PIA)' : list.length > 1 ? ' (Split Deals)' : '';
      for (const part of list) {
        const owner = part && part.owner ? String(part.owner).trim() : '';
        if (!owner) continue;
        const entry = `${owner}${tag}`;
        // A split whose two halves share an owner would otherwise print the same line twice.
        if (!out.includes(entry)) out.push(entry);
      }
    }
    return out.length ? out.join(', ') : raw;
  }, [payment]);
  const hasInventory = !!(payment.pm_name || hasPlacedValue);
  // [INFO-GRID 2026-06-24] Column template shared by BOTH header rows (they live in
  // ONE grid). 148px heading gutter + one track per data field, each
  // minmax(max-content, 1fr): max-content guarantees the label/value is NEVER
  // clipped, 1fr lets the tracks grow to fill the bar. The Deal-Owner track is
  // added only when that field renders — 7 data fields (Payment Date, Website, Email,
  // Payment Amount, Account, Refunded, Yet to Map), or 8 with Deal Owner.
  const infoCols = `148px repeat(${showDealOwner ? 8 : 7}, minmax(max-content, 1fr))`;

  // [INV-NOTE 2026-06-24] The full-width status SENTENCE below the header summarises
  // the whole popup so it can be read on its own (see the InfoNote render). These are
  // the derived pieces it needs:
  //  • mappedCategories — the categories THIS payment's amount currently lands in
  //    (recomputed live as the grid is edited; '' when nothing is mapped yet).
  //  • history — prior payments in the same (website, month) bucket, summarised to a
  //    total + distinct categories; drives the "already mapped" clause.
  // Everything else (amount, site, account, deal owner, refund, inventory, ROW) is
  // read straight off `payment` / live state in the render. "Last updated" stays in
  // the footer line only — intentionally not repeated in this sentence.
  const mappedCategories = useMemo(
    () => listCategoriesWithDept(fields),
    [fields],
  );
  const noteAmount = fmt(Number(payment.amount));
  const isMapped = totalMapped > 0;
  // Over-mapped: the allocations sum to MORE than the (post-refund) payment, so the
  // save is blocked (same −half-cent threshold as handleSave). The status clause
  // must flag this as an error instead of cheerfully reporting the breakdown.
  const isOverMapped = remaining < -0.005;
  const noteSite = payment.website || payment.email || 'this account';
  const noteAccount = payment.account_name || '';
  const history = useMemo(() => {
    const m = new Map<string, number>();
    for (const sib of references) {
      for (const [label, amt] of aggregateByCategory(sib as any)) {
        m.set(label, (m.get(label) ?? 0) + amt);
      }
    }
    return {
      has: m.size > 0,
      total: [...m.values()].reduce((a, b) => a + b, 0),
      cats: listCategories(m),
    };
  }, [references]);

  // [MAP-VALIDATION] 2026-06-23 — true when the user has provided SOMETHING worth
  // saving: any monetary allocation (incl. Recovery, via totalMapped) or a PIA
  // Month/Dept selection. Used to block an empty Save on a row that was never
  // mapped (see handleSave). PIA Month/Dept count so a partial entry is never
  // silently rejected.
  // [ROW-AUTO 2026-08-07] The ROW flag no longer counts as user input — it is not
  // entered here any more, so it can't be the only thing a Save would persist.
  const hasAnyInput = useMemo(() => {
    if (totalMapped > 0) return true;
    for (const dept of DEPTS) {
      if (fields[fieldKey(dept, 'pia_month')]) return true;
      if (fields[fieldKey(dept, 'pia_dept')]) return true;
    }
    return false;
  }, [totalMapped, fields]);

  // [MAP-VALIDATION] 2026-06-23 — did this payment ALREADY carry a mapping when
  // the modal opened? Read straight off the payment's own stored values (any
  // monetary allocation, Recovery, the ROW flag, or a PIA Month/Dept). We must
  // NOT use `mapped_status` here: it is only 'TRUE' when the payment is FULLY
  // mapped, so a partial mapping (e.g. $1 of $300) reads 'FALSE' and would wrongly
  // be treated as never-mapped — blocking the legitimate Clear → Save un-map flow.
  // When true, an empty Save is allowed (it clears / un-maps the payment).
  const initiallyMapped = useMemo(() => {
    for (const dept of DEPTS) {
      for (const row of ROWS) {
        const v = (payment as any)[fieldKey(dept, row.suffix)];
        if (v == null || v === '') continue;
        if (row.type === 'money' && Number(v) === 0) continue;
        return true;
      }
    }
    const rec = (payment as any).recovery;
    if (rec != null && rec !== '' && Number(rec) !== 0) return true;
    const shop = (payment as any).shopify_amount; // [SHOPIFY 2026-10-19]
    if (shop != null && shop !== '' && Number(shop) !== 0) return true;
    if ((payment as any).row_status === 'ROW') return true;
    return false;
  }, [payment]);

  const setField = (key: string, val: string) => {
    setFields((prev) => {
      const next = { ...prev, [key]: val };
      // [PIA-REVEAL 2026-06-24] When a department's PIA Amount is cleared (or
      // zeroed), drop its PIA Month / PIA Dept too. Those rows hide without a PIA
      // amount, so leaving stale selections would orphan them — invisible to the
      // user yet still tripping the all-or-nothing-per-department PIA validation
      // on save. Dept names have no underscores, so the suffix split is reliable.
      if (key.endsWith('_pia_amount')) {
        const amt = parseFloat(val);
        if (isNaN(amt) || amt <= 0) {
          const dept = key.slice(0, -'_pia_amount'.length);
          next[fieldKey(dept, 'pia_month')] = '';
          next[fieldKey(dept, 'pia_dept')] = '';
        }
        // [PIA-RENEWAL 2026-07-10] The single PIA Renewal Amount box hides once NO
        // department has a positive PIA amount — drop its value too so a stale renewal
        // isn't saved (or blocked by its mandatory check) invisibly, mirroring how the
        // per-department Month/Dept are cleared above.
        const anyPia = DEPTS.some((d) => {
          const a = parseFloat(next[fieldKey(d, 'pia_amount')]);
          return !isNaN(a) && a > 0;
        });
        if (!anyPia) next.pia_renewal_amount = '';
      }
      return next;
    });
  };

  const handleClear = () => {
    const cleared: Record<string, any> = {};
    // [RANKING-ONLY 2026-10-27] Clear empties the boxes on screen. Old Scale Performance / Tech / Creative money keeps
    // its value: it has no box, and Save sends it back as it is, so Clear then Save can never wipe it.
    for (const k of Object.keys(fields)) cleared[k] = isOldBrandKey(k) ? fields[k] : '';
    setFields(cleared);
    // [ROW-AUTO 2026-08-07] Clear wipes the MAPPING only. It no longer touches the ROW
    // flag: that reflects which team the deal owner is on, which a mapping reset does
    // not change — and the save can't send row_status any more in any case.
  };

  // [RANKING-ONLY 2026-10-27] Money this payment already holds on the three brands no longer mapped to, for the
  // read-only "Old mapping" line. Read from the SAVED row: there is no box for it, so it cannot change here.
  const oldMoney = useMemo(() => {
    const out: { key: string; dept: string; label: string; amount: number }[] = [];
    for (const dept of OLD_DEPTS) {
      for (const row of ROWS) {
        if (row.type !== 'money') continue;
        const key = fieldKey(dept, row.suffix);
        const amount = Number((payment as any)[key]) || 0;
        if (amount > 0) out.push({ key, dept, label: row.label, amount });
      }
    }
    return out;
  }, [payment]);

  // [RANKING-ONLY 2026-10-27] "Fill rest" puts everything still left to map into that box, on top of what it already
  // holds. Most payments go to ONE type (92 % of Jul–Oct 2026), so that is one click, then Save.
  const canFill = remaining > 0.005;
  const fillRest = (key: string) => {
    if (!canFill) return;
    setField(key, String(roundCents((parseFloat(fields[key]) || 0) + remaining)));
  };

  // [PAYMENT-DELETE 2026-07-17] Confirmed delete. onDelete soft-deletes the row (→ Bin);
  // on success the parent closes this modal + refreshes the table, so we don't reset local
  // state here. On failure we surface the server's message and keep the modal open so the
  // admin can retry or cancel.
  const doDelete = async () => {
    if (!onDelete) return;
    setDeleting(true);
    try {
      await onDelete(payment.id);
    } catch (err: any) {
      const data = err?.response?.data;
      toastError(data?.message || data?.error || 'Could not delete the payment');
      setShowDeleteConfirm(false);
      setDeleting(false);
    }
  };

  const doSave = async () => {
    setSaving(true);
    setShowConfirm(false);
    try {
      const payload: Record<string, any> = {};
      // [RETENTION-EDIT 2026-07-03] Send retention ONLY when it's editable on this
      // payment (PM name + placed value) AND the user actually changed it. On a
      // restricted payment, or an untouched auto-filled value, retention is omitted so
      // the server never sets ranking_retention_locked=1 by echoing the auto-filled
      // amount back — that lock would freeze retention against refunds/placed-value
      // changes (the reason it was blanket-omitted before). A genuine manual edit DOES
      // flow through and lock the row, which is correct: the auto-allocator then leaves
      // the user's value alone (clearing it sends null, which unlocks + auto-refills).
      const initialRetention = (dept: string) => {
        const v = (payment as any)[fieldKey(dept, 'retention')];
        return v == null || v === '' || Number(v) === 0 ? '' : String(v);
      };
      const sendRetention = canEditRetention && (
        DEPTS.some((dept) => (fields[fieldKey(dept, 'retention')] ?? '') !== initialRetention(dept))
        // [INV-SPLIT 2026-10-07] A PM change alone (same amount, another PM) is a real edit too.
        || (isSplit && sharesChanged));
      // [RANKING-ONLY 2026-10-27] Still every brand's key, as before: the three old brands carry their SAVED values
      // (no box can change them, Clear keeps them), so the server writes back exactly what it holds, and the deal
      // popup (PaymentsPage → DealCreationModal adds up all four brands from this payload) sees the whole payment.
      for (const dept of DEPTS) {
        for (const row of ROWS) {
          if (row.suffix === 'retention' && !sendRetention) continue;
          const k = fieldKey(dept, row.suffix);
          const raw = fields[k];
          if (row.type === 'money') {
            payload[k] = raw === '' ? null : parseFloat(raw) || 0;
          } else {
            payload[k] = raw === '' ? null : raw;
          }
        }
      }
      const rawRec = fields.recovery;
      payload.recovery = rawRec === '' || rawRec == null ? null : parseFloat(rawRec) || 0;
      // [PIA-RENEWAL 2026-07-10] Single per-payment renewal amount (same shape as
      // recovery). Sent as null when empty — clearing the PIA clears this too (setField).
      const rawRenew = fields.pia_renewal_amount;
      payload.pia_renewal_amount = rawRenew === '' || rawRenew == null ? null : parseFloat(rawRenew) || 0;
      // [SHOPIFY 2026-10-19] Sent only once the server has the column (the row then carries the key).
      if ('shopify_amount' in (payment as any)) {
        const rawShop = fields.shopify_amount;
        payload.shopify_amount = rawShop === '' || rawShop == null ? null : parseFloat(rawShop) || 0;
      }
      // [INV-SPLIT 2026-10-07] The PM shares travel with the retention (the server checks each PM's
      // limit and refuses a retention that no PM was given). Cleared retention → no shares.
      if (isSplit && sendRetention) {
        payload.retention_pm_splits = retentionTotal > 0.005
          ? pmShares.filter((s) => Number(s.amount) > 0.005).map((s) => ({ pm: s.pm, amount: roundCents(Number(s.amount)) }))
          : [];
      }
      // [ROW-AUTO 2026-08-07] row_status is NOT sent any more — it's derived from the
      // deal owner server-side, and the mapping endpoint no longer accepts it.
      await onSave(payment.id, payload);
    } catch (err: any) {
      // [RETENTION-CAP 2026-07-03] The server refused the save — e.g. the bucket's
      // total retention would exceed the placed value (409 RETENTION_EXCEEDS_PLACED),
      // or the month is read-only. Surface the server's own message as the standard
      // top-center toast and keep the modal open so the value can be corrected.
      const data = err?.response?.data;
      toastError(data?.message || data?.error || 'Could not save the mapping');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => {
    // [MAP-VALIDATION] 2026-06-23 — a brand-new mapping must carry at least one
    // value. If nothing was entered, keep the modal open and surface a top-center
    // error toast instead of silently saving an empty mapping. A payment that
    // already had a mapping is exempt (see `initiallyMapped`): saving it empty —
    // i.e. Clear → Save — intentionally clears/un-maps it.
    if (!hasAnyInput && !initiallyMapped) {
      toastError('Value required to save the mapping');
      return;
    }
    // [MAP-VALIDATION] 2026-06-23 — PIA is all-or-nothing PER DEPARTMENT: PIA
    // Amount, PIA Month and PIA Dept must be filled together. If a department has
    // some-but-not-all three, block the save (nothing is sent to the API) and keep
    // the modal open. Also catches a lone Month/Dept picked without an amount.
    const piaIncomplete = DEPTS.some((dept) => {
      const amt = parseFloat(fields[fieldKey(dept, 'pia_amount')]);
      const filled = [
        !isNaN(amt) && amt > 0,
        !!fields[fieldKey(dept, 'pia_month')],
        !!fields[fieldKey(dept, 'pia_dept')],
      ].filter(Boolean).length;
      return filled > 0 && filled < 3;
    });
    if (piaIncomplete) {
      toastError('Enter the complete PIA details to save');
      return;
    }
    // [PIA-RENEWAL 2026-07-10] PIA Renewal Amount is the 4th mandatory PIA field: once any
    // PIA is present (some department has a PIA Amount), the single renewal box must be
    // filled — it's the value the inventory renewal row uses. Same completeness message.
    if (hasPiaAmount && !(parseFloat(fields.pia_renewal_amount) > 0)) {
      toastError('Enter the complete PIA details to save');
      return;
    }
    // [MAP-VALIDATION] 2026-06-23 — never persist a mapping that exceeds the
    // payment's (post-refund) amount. `remaining` is finalAmount - totalMapped, so
    // a negative remaining means the entered values are over the available amount:
    // block the save outright (nothing is sent to the API) and show a top-center
    // error. The half-cent epsilon ignores float noise at an exact match.
    if (remaining < -0.005) {
      toastError('Mapped amount exceeds the payment amount');
      return;
    }
    // [INV-SPLIT 2026-10-07, user] On a split website every retention amount needs a PM first; the
    // popup's answer resumes this save (see the effect below).
    if (isSplit && retentionTotal > 0.005 && !sharesMatch) {
      openPmStep(RANKING, true); // [SPLIT-RET-BY-PM 2026-10-08] the answer resumes this save [RANKING-ONLY 2026-10-27]
      return;
    }
    if (remaining > 0 && totalMapped > 0) {
      setShowConfirm(true);
    } else {
      doSave();
    }
  };
  // [INV-SPLIT 2026-10-07] Latest handleSave for the resume effect — a ref, so the effect does not
  // re-run on every render (and the CRA build's hook-dependency rule stays satisfied).
  const handleSaveRef = useRef(handleSave);
  handleSaveRef.current = handleSave;
  useEffect(() => {
    if (!resumeSaveRef.current || pmStep) return;
    if (isSplit && retentionTotal > 0.005 && sharesMatch) {
      resumeSaveRef.current = false;
      handleSaveRef.current();
    }
  }, [pmShares, fields, pmStep, isSplit, retentionTotal, sharesMatch]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    /* [INV-SPLIT FIX 2026-10-08, user] While "Which PM?" is open, a click on this background never
       closes Edit Mapping. That popup opens on BLUR — while the mouse button is still down on another
       box — so the release lands on the popup, and the browser sends the click to the nearest element
       holding both: this background. It used to close the window and the popup with it. */
    if (pmStep) return;
    if (e.target === e.currentTarget) onClose();
  };

  /* ───── [RANKING-ONLY 2026-10-27] what the "Two groups" cards show ───── */

  // Retention: locked with "Auto" unless the payment has a PM and a placed value, or the website is split between PMs
  // (canEditRetention); on a split website a click opens "Retention by PM".
  const retKey = fieldKey(RANKING, 'retention');
  const retLocked = !canEditRetention;
  const retHint = retLocked
    ? 'Every month · filled by itself'
    : isSplit
      ? 'Every month · click to give it to the PMs'
      : 'Every month · filled by itself, you can change it';

  // PIA: Months paid counts the payment's OWN month as 1 (the rule kept on 2026-08-26, see piaLandingMonth), and the
  // line under the boxes says it in words: "Pays for Oct 2026 to Nov 2026 · next inventory row: Dec 2026".
  const piaKey = fieldKey(RANKING, 'pia_amount');
  const termKey = fieldKey(RANKING, 'pia_month');
  const piaForKey = fieldKey(RANKING, 'pia_dept');
  const term = parseInt(String(fields[termKey] ?? ''), 10);
  const hasTerm = Number.isFinite(term) && term >= 1;
  const piaFor = String(fields[piaForKey] || '');
  const thisMonth = monthPlus(payment.month_str, 0);
  let piaNote = 'How many months does this payment pay for? Its own month counts as 1.';
  if (piaFor === 'Sales') piaNote = 'A Sales PIA makes no inventory row.';
  else if (hasTerm && thisMonth) {
    const covers = term === 1
      ? `Pays for ${thisMonth} only`
      : `Pays for ${thisMonth} to ${monthPlus(payment.month_str, term - 1)}`;
    piaNote = `${covers} · next inventory row: ${piaLandingMonth(payment.month_str, String(term))}`;
  }

  // Shopify: one box, only on a Shopify payout or a payment that already holds a Shopify amount ([SHOPIFY 2026-10-19]).
  const showShopify = Number((payment as any).is_shopify_payout) === 1 || Number((payment as any).shopify_amount || 0) !== 0;

  // One line of a card: the name + a plain-words hint, the box, then "Fill rest".
  const moneyLine = (key: string, label: string, hint: string) => (
    <MapRow key={key}>
      <div>
        <LineLabel htmlFor={`map-${key}`}>{label}</LineLabel>
        <LineHint>{hint}</LineHint>
      </div>
      <MoneyInput id={`map-${key}`} value={fields[key] || ''} onChange={(v) => setField(key, v)} />
      <FillBtn
        type="button"
        onClick={() => fillRest(key)}
        disabled={!canFill}
        aria-label={`Fill rest into ${label}`}
        title={canFill ? `Put the ${fmt(remaining)} left to map into ${label}` : 'Nothing is left to map'}
      >
        Fill rest
      </FillBtn>
    </MapRow>
  );

  // Rendered through a portal to <body> so the fixed overlay is positioned
  // against the viewport, not trapped by the Payments page container's
  // backdrop-filter (which would otherwise centre the modal in the middle of the
  // full scrollable page instead of the visible area).
  // [FLUID-3 2026-10-12] Inert while it closes: the window stays on screen for the 0.18 s exit, and nothing in it
  // (or in a popup over it) may be used then.
  return createPortal(
    <Overlay onClick={handleOverlayClick} $open={open} inert={closing}>
      <Modal $open={open}>
        <ModalHeader>
          {/* [RANKING-ONLY 2026-10-27] The title + "Mapping to [The Scale Ranking]" on one line: new money goes to
              Scale Ranking only, so the brand is named once, here (it was four logo column headers). */}
          <TitleRow>
            <ModalTitle>Edit Mapping</ModalTitle>
            <BrandPill title="New money is mapped to Scale Ranking only">
              <span>Mapping to</span>
              <RankingLogo role="img" aria-label="The Scale Rankings" />
            </BrandPill>
          </TitleRow>
          {/* [INFO-GRID 2026-06-24] Payment Info + Inventory Info share ONE grid so
              the columns align AND each sizes to its own content (no clipped labels
              like "PAYMENT AMOU…"). Inventory's PM Name / Placed Value auto-flow onto
              a fresh row under Payment's Website / Email; a full-width hairline splits
              the two. The optional sentence below summarises where the money lands. */}
          <InfoGrid style={{ '--info-cols': infoCols } as React.CSSProperties}>
            {/* — Payment Info row — */}
            <SectionHead>Payment Info</SectionHead>
            <Field><span>Payment Date</span> <strong>{fmtDate(payment.payment_date)}</strong></Field>
            <Field><span>Website</span> <strong>{payment.website || '—'}</strong></Field>
            <Field><span>Email</span> <strong>{payment.email || '—'}</strong></Field>
            <Field><span>Payment Amount</span> <strong>{fmt(Number(payment.amount))}</strong></Field>
            <Field><span>Account</span> <strong>{payment.account_name}</strong></Field>
            <Field><span>Refunded</span> <strong style={{ color: refundedDisplay > 0 ? '#dc2626' : undefined }}>{fmt(refundedDisplay)}</strong></Field>
            <Field><span>Yet to Map</span> <RemainingBadge $alert={remainingAlert}>{fmt(remaining)}</RemainingBadge></Field>
            {/* The HubSpot owner name(s) the deal was created on — labelled in HubSpot
                orange. Shown ONLY when there IS a deal owner: an unmapped row (no deal)
                and a retention-only mapping both leave it blank, so the field (and its
                track, via infoCols) is dropped rather than rendering a bare "—". */}
            {showDealOwner && (
              <Field><HubspotLabel>Deal Owner</HubspotLabel> <strong>{dealOwnerLabelled}</strong></Field>
            )}

            {/* — Inventory Info row — dropped entirely when neither PM Name nor Placed
                Value exists. The full-width divider + heading start a fresh grid row,
                so its two fields land under the first two Payment data columns: PM Name
                under Payment Date and Placed Value under Website. */}
            {hasInventory && (
              <>
                <SectionDivider />
                <SectionHead>Inventory Info</SectionHead>
                <Field><span>PM Name</span> <strong>{payment.pm_name || '—'}</strong></Field>
                {/* [INV-SPLIT 2026-10-07] A split website shows each PM's part, in the PM Name order. */}
                <Field><span>Placed Value</span> <strong>{isSplit
                  ? splitParts.map((p) => fmt(Number(p.amount))).join(' / ')
                  : hasPlacedValue ? fmt(Number(payment.placed_value)) : '—'}</strong></Field>
              </>
            )}

          </InfoGrid>
          {/* — Status sentence — full-width block below the grid; its top border is
              the divider (matching the row hairlines). It summarises the popup in one
              sentence: amount → site → account → deal owner → refund (red) → ROW →
              inventory → live mapping status (green when mapped within the payment;
              red when unmapped, partially short, or over-mapped/un-saveable) →
              already-mapped history. Each clause drops out when its data is absent.
              String literals carry the spacing so JSX won't collapse it; only the
              refund + mapping-status clauses are coloured. ("Last updated" stays in
              the footer line, not here.) */}
          <InfoNote>
            {'The current payment received is '}<strong>{noteAmount}</strong>
            {' for '}{noteSite}
            {noteAccount && <>{' via '}{noteAccount}</>}
            {showDealOwner && <>{' (deal owner '}{dealOwnerLabelled}{')'}</>}
            {refundedDisplay > 0.005 && (
              <>{', of which '}<span className="bad">{fmt(refundedDisplay)}</span>{' has since been refunded'}</>
            )}
            {/* [ROW-AUTO 2026-08-07] Reads the payment's STORED flag now, not a local
                checkbox — the sentence still reports ROW, it just isn't editable here. */}
            {(payment as any).row_status === 'ROW' && ', flagged ROW'}
            {hasInventory && (
              <>
                {', with '}
                {payment.pm_name && <>{'PM '}{payment.pm_name}</>}
                {payment.pm_name && hasPlacedValue ? ' and ' : ''}
                {hasPlacedValue && <>{'a placed value of '}<strong>{fmt(Number(payment.placed_value))}</strong></>}
              </>
            )}
            {'; '}
            {!isOverMapped && 'the amount '}
            {isOverMapped ? (
              <span className="bad">
                {'the mapped total of '}{fmt(totalMapped)}{' exceeds the payment amount by '}{fmt(Math.abs(remaining))}{", so it can't be saved"}
              </span>
            ) : isMapped ? (
              <>
                <span className="ok">{'is mapped to '}{mappedCategories}</span>
                {remaining > 0.005 && <span className="bad">{', with '}{fmt(remaining)}{' still to map'}</span>}
              </>
            ) : (
              <span className="bad">still needs to be mapped</span>
            )}
            {history.has && (
              <>{', and '}<strong>{fmt(history.total)}</strong>{' was already mapped for this website this month ('}{history.cats}{')'}</>
            )}
            {'.'}
          </InfoNote>
        </ModalHeader>

        <ModalBody>
          {/* [INV-SPLIT 2026-10-07, user] The PMs of a split website, what each already holds this month,
              and who this payment's retention goes to. Retention typed here is given to a PM in the popup. */}
          {isSplit && (
            <SplitPanel>
              <div>
                <strong>Split between PMs.</strong>{' '}
                {splitParts.map((p, i) => {
                  const savedHere = savedShares.filter((s) => s.pm === p.pm).reduce((t, s) => t + Number(s.amount), 0);
                  const here = pmShares.filter((s) => s.pm === p.pm).reduce((t, s) => t + Number(s.amount), 0);
                  const mapped = Math.max(0, roundCents(Number(p.mapped) - savedHere + here));
                  return (
                    <React.Fragment key={p.pm}>
                      {i > 0 && ' · '}
                      {p.pm}: part {fmt(Number(p.amount))}, {fmt(mapped)} mapped
                    </React.Fragment>
                  );
                })}
              </div>
              {/* [INV-SPLIT 2026-10-08, user] ONLY the Split line — no second line of hints or of this payment's
                  shares ("only this line will be there … and nothing else"). The PM is asked for on Save. */}
            </SplitPanel>
          )}
          {references.length > 0 && (
            <RefPanel>
              <RefRow>
                {[...references]
                  // Earliest payment date first, later ones after.
                  .sort((a, b) =>
                    new Date(a.payment_date || 0).getTime() - new Date(b.payment_date || 0).getTime())
                  .map((sib, idx) => {
                    const chips = buildRefChips(sib);
                    // [REF-REFUNDED 2026-08-21] A fully refunded/disputed sibling with nothing
                    // mapped is labelled as exactly that, not "Already mapped" - see
                    // classifyReference for why the mapped flag lies here.
                    const ref = classifyReference(sib);
                    const wasLost = ref.kind !== 'mapped';
                    return (
                      <React.Fragment key={sib.id}>
                        {idx > 0 && <RefDivider>|</RefDivider>}
                        <RefEntry>
                          <RefBadge $tone={wasLost ? 'refunded' : 'mapped'}>
                            {ref.kind === 'disputed' ? 'Disputed' : ref.kind === 'refunded' ? 'Refunded' : 'Already mapped'}
                          </RefBadge>
                          <span>{fmtDate(sib.payment_date)}</span>
                          <span className="sep">·</span>
                          <span className="site">{sib.website || sib.email}</span>
                          <span className="sep">·</span>
                          <span className="amt">{fmt(Number(sib.amount))}</span>
                          {(sib as any).row_status === 'ROW' && <RowFlag>ROW</RowFlag>}
                          {wasLost ? (
                            <span style={{ fontSize: 11, opacity: 0.7 }}>
                              {ref.kind === 'disputed' ? 'Disputed' : 'Refunded'} in full ({fmt(ref.lostAmount)}) — nothing was mapped
                            </span>
                          ) : chips.length > 0 ? (
                            <ChipGroup>
                              {chips.map((c) => (
                                <Chip key={c.key} dept={c.dept}>
                                  <ChipDot dept={c.dept} />
                                  <ChipLabel>{c.label}</ChipLabel>
                                  <ChipValue dept={c.dept}>{c.value}</ChipValue>
                                </Chip>
                              ))}
                            </ChipGroup>
                          ) : (
                            <span style={{ fontSize: 11, opacity: 0.5 }}>No category breakdown</span>
                          )}
                          {/* A partly refunded payment that still carries its mapping keeps the
                              green badge but says so, so the history is never read as whole. */}
                          {!wasLost && ref.partial && (
                            <span style={{ fontSize: 11, opacity: 0.7 }}>−{fmt(ref.lostAmount)} refunded</span>
                          )}
                        </RefEntry>
                      </React.Fragment>
                    );
                  })}
              </RefRow>
            </RefPanel>
          )}
          {/* [RANKING-ONLY 2026-10-27] Old Scale Performance / Tech / Creative money on this payment: kept as it is,
              read-only (no box can change it, Clear keeps it, Save sends it back unchanged). */}
          {oldMoney.length > 0 && (
            <OldMoneyStrip>
              <LockIcon size={16} />
              <div className="what">
                <strong>Old mapping, kept as it is</strong>
                <span>Scale Performance, Tech and Creative are not used any more. This money still counts in the total, and Save keeps it.</span>
              </div>
              <OldChips>
                {oldMoney.map((o) => (
                  <Chip key={o.key} dept={o.dept}>
                    <ChipDot dept={o.dept} />
                    <ChipLabel>{OLD_BRAND_NAMES[o.dept] ?? o.dept} · {o.label}</ChipLabel>
                    <ChipValue dept={o.dept}>{fmt(o.amount)}</ChipValue>
                  </Chip>
                ))}
              </OldChips>
            </OldMoneyStrip>
          )}

          {/* [RANKING-ONLY 2026-10-27, user] Layout 2 "Two groups": who the money comes from (a current client = PM
              side, a new client = Sales side), then PIA and the rest. Every box is Scale Ranking's (RANKING). */}
          <GroupGrid>
            <GroupCard aria-labelledby="map-g-current">
              <GroupHead>
                <h3 id="map-g-current">Current client</h3>
                <SideBadge $side="pm">PM side</SideBadge>
              </GroupHead>
              <GroupNote>A client we already work with.</GroupNote>
              <MapRow>
                <div>
                  <LineLabel htmlFor="map-retention">Retention</LineLabel>
                  <LineHint>{retHint}</LineHint>
                </div>
                {/* [RETENTION-EDIT 2026-07-03] Editable only with a PM + placed value, else locked ("Auto");
                    [SPLIT-RET-BY-PM 2026-10-08] on a split website a click opens "Retention by PM". */}
                <RetentionBox>
                  <MoneyInput
                    id="map-retention"
                    value={fields[retKey] || ''}
                    disabled={retLocked}
                    title={retLocked ? 'Filled by itself. It can be changed only when this payment has a PM and a placed value.' : undefined}
                    onChange={(v) => setField(retKey, v)}
                    retention={isSplit}
                    onOpen={isSplit && !retLocked ? () => openPmStep(RANKING) : undefined}
                  />
                  {retLocked && <AutoTag aria-hidden="true"><LockIcon /><span>Auto</span></AutoTag>}
                </RetentionBox>
              </MapRow>
              {moneyLine(fieldKey(RANKING, 'r_so'), 'Recurring Upsell', 'Extra work · every month')}
              {moneyLine(fieldKey(RANKING, 'o_so'), 'One-Time Upsell', 'Extra work · paid once')}
            </GroupCard>

            <GroupCard aria-labelledby="map-g-new">
              <GroupHead>
                <h3 id="map-g-new">New client</h3>
                <SideBadge $side="sales">Sales side</SideBadge>
              </GroupHead>
              <GroupNote>A client paying us for the first time.</GroupNote>
              {moneyLine(fieldKey(RANKING, 'r_new_sale'), 'Recurring New Sale', 'Pays every month')}
              {moneyLine(fieldKey(RANKING, 'o_new_sale'), 'One-Time New Sale', 'Pays once')}
            </GroupCard>

            <GroupCard aria-labelledby="map-g-pia">
              <GroupHead>
                <h3 id="map-g-pia">Paid in advance (PIA)</h3>
              </GroupHead>
              <GroupNote>One payment that covers more than one month.</GroupNote>
              {moneyLine(piaKey, 'PIA Amount', 'The amount paid in advance')}
              {/* [PIA-REVEAL 2026-06-24] The rest of the PIA shows once an amount is typed, and all four are needed to
                  save ([MAP-VALIDATION], [PIA-RENEWAL 2026-07-10]). [RANKING-ONLY 2026-10-27] PIA Month is "Months
                  paid" with minus / plus (1 to 12) and PIA Dept is "PIA for" Sales | PM: the same values the two
                  dropdowns saved. */}
              {hasPiaAmount && (
                <PiaPanel>
                  <PiaLine>
                    <span id="map-pia-months">Months paid</span>
                    <Stepper role="group" aria-labelledby="map-pia-months">
                      <StepBtn
                        type="button"
                        aria-label="One month less"
                        disabled={!hasTerm}
                        onClick={() => setField(termKey, term > 1 ? String(term - 1) : '')}
                      >
                        −
                      </StepBtn>
                      <StepValue aria-live="polite">{hasTerm ? (term === 1 ? '1 month' : `${term} months`) : 'Pick'}</StepValue>
                      <StepBtn
                        type="button"
                        aria-label="One month more"
                        disabled={hasTerm && term >= 12}
                        onClick={() => setField(termKey, String(hasTerm ? Math.min(12, term + 1) : 1))}
                      >
                        +
                      </StepBtn>
                    </Stepper>
                  </PiaLine>
                  <PiaLine>
                    <span id="map-pia-for">PIA for</span>
                    <Segmented role="group" aria-labelledby="map-pia-for">
                      <SegThumb aria-hidden="true" $at={piaFor === 'Sales' ? 'left' : piaFor === 'PM' ? 'right' : 'none'} />
                      <Seg type="button" $active={piaFor === 'Sales'} aria-pressed={piaFor === 'Sales'} onClick={() => setField(piaForKey, 'Sales')}>
                        Sales
                      </Seg>
                      <Seg type="button" $active={piaFor === 'PM'} aria-pressed={piaFor === 'PM'} onClick={() => setField(piaForKey, 'PM')}>
                        PM
                      </Seg>
                    </Segmented>
                  </PiaLine>
                  <RenewRow>
                    <div>
                      <LineLabel htmlFor="map-pia-renewal">Renewal amount</LineLabel>
                      <LineHint>The amount for the next inventory row</LineHint>
                    </div>
                    <MoneyInput
                      id="map-pia-renewal"
                      value={fields.pia_renewal_amount || ''}
                      onChange={(v) => setField('pia_renewal_amount', v)}
                    />
                  </RenewRow>
                  <PiaNote $muted={piaFor === 'Sales'}>{piaNote}</PiaNote>
                </PiaPanel>
              )}
            </GroupCard>

            <GroupCard $top aria-labelledby="map-g-other">
              <GroupHead>
                <h3 id="map-g-other">Other</h3>
              </GroupHead>
              <GroupNote>Money that is not a sale.</GroupNote>
              {moneyLine('recovery', 'Recovery', 'Money we got back, like a dispute won')}
              {showShopify && moneyLine('shopify_amount', 'Shopify', 'Shopify payout · filled by itself')}
            </GroupCard>
          </GroupGrid>

          {(() => {
            const who = lastEdit?.userName || lastEdit?.userEmail;
            if (!who) return null;
            const when = fmtWhen(lastEdit?.at);
            return (
              <LastUpdated>
                Last updated by <strong>{who}</strong>
                {when && <span className="when"> · {when}</span>}
              </LastUpdated>
            );
          })()}
        </ModalBody>

        <ModalFooter>
          {/* [ROW-AUTO 2026-08-07] The ROW checkbox that stood here is GONE. It was
              manual and kept being forgotten, which booked ROW money into the ordinary
              sales buckets. `row_status` is now derived from the deal owner's HubSpot
              team when a deal is saved (routes/hubspot.ts), so there is nothing to tick.
              The status sentence above still reports the flag. */}
          {/* [PAYMENT-DELETE 2026-07-17] Admin-only, sits BEFORE Clear. Removes the whole
              payment (→ Bin) after a confirm — distinct filled-red so it can't be confused
              with Clear (which only wipes the mapping). Hidden entirely for non-admins
              (onDelete is passed only for admins). [RANKING-ONLY 2026-10-27] Now on the far left, away from Save. */}
          {onDelete && (
            <DeleteBtn $variant="delete" onClick={() => setShowDeleteConfirm(true)} disabled={deleting}>
              Delete Payment
            </DeleteBtn>
          )}
          <Btn $variant="clear" onClick={handleClear}>Clear</Btn>
          <Btn onClick={onClose}>Cancel</Btn>
          <Btn $variant="save" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Btn>
        </ModalFooter>
      </Modal>

      {/* [INV-SPLIT 2026-10-07, user] "Retention by PM" — [SPLIT-RET-BY-PM 2026-10-08] an amount per PM.
          [FLUID-3 2026-10-12] Held mounted through its close (closing = no pmStep any more). */}
      {pmPres.mounted && (
        <RetentionPmPopup
          key={pmSeq}
          closing={!pmStep}
          website={payment.website || payment.email || 'this website'}
          paymentAmount={roundCents(finalAmount)}
          maxTotal={stepMaxTotal}
          parts={stepParts}
          initial={pmShares}
          onCancel={() => { setPmStep(null); resumeSaveRef.current = false; }}
          onConfirm={confirmPmStep}
        />
      )}

      {/* [FLUID-3 2026-10-12] Each confirm box opens and closes with the fluid motion; inert while it closes. */}
      {confirmPres.mounted && (
        <ConfirmOverlay onClick={() => setShowConfirm(false)} $open={showConfirm && confirmPres.entered} inert={!showConfirm}>
          <ConfirmBox onClick={(e) => e.stopPropagation()} $open={showConfirm && confirmPres.entered}>
            <ConfirmMsg>Amount to be saved is less than the actual amount. Do you still want to save it?</ConfirmMsg>
            <ConfirmActions>
              <Btn onClick={() => setShowConfirm(false)}>Cancel</Btn>
              <Btn $variant="save" onClick={doSave}>Yes</Btn>
            </ConfirmActions>
          </ConfirmBox>
        </ConfirmOverlay>
      )}

      {/* [PAYMENT-DELETE 2026-07-17] Delete confirmation — names the row (website + amount)
          so the admin can see they're removing the right one before it's gone. */}
      {deletePres.mounted && (
        <ConfirmOverlay onClick={() => { if (!deleting) setShowDeleteConfirm(false); }} $open={showDeleteConfirm && deletePres.entered} inert={!showDeleteConfirm}>
          <ConfirmBox onClick={(e) => e.stopPropagation()} $open={showDeleteConfirm && deletePres.entered}>
            <ConfirmMsg>
              Delete this payment{payment.website ? <> for <strong>{payment.website}</strong></> : ''} of{' '}
              <strong>{fmt(Number(payment.amount))}</strong>? It moves to the Bin, where an admin can restore it.
            </ConfirmMsg>
            <ConfirmActions>
              <Btn onClick={() => setShowDeleteConfirm(false)} disabled={deleting}>Cancel</Btn>
              <Btn $variant="delete" onClick={doDelete} disabled={deleting}>
                {deleting ? 'Deleting…' : 'Delete Payment'}
              </Btn>
            </ConfirmActions>
          </ConfirmBox>
        </ConfirmOverlay>
      )}
    </Overlay>,
    document.body
  );
};

export default MappingModal;
